"use client"

import { useState, useEffect } from "react"
import Swal from "sweetalert2"
import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
import Sidebar from "./Sidebar"
import DashNavbar from "./DashNavbar"
import Select from "react-select"
import axios from "axios"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"

const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

const RATING_SCALE_OPTIONS = [
  { value: "Exceptional", label: "Exceptional", numeric: 5 },
  { value: "Above Average", label: "Above Average", numeric: 4 },
  { value: "Average", label: "Average", numeric: 3 },
  { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
  { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
]

const RECOMMENDATION_OPTIONS = [
  { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
  { value: "RECOMMEND", label: "Recommend" },
  { value: "NEUTRAL", label: "Neutral" },
  { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
]

const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = false;
    return acc;
}, {});

export default function EvaluationForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [rawInterviews, setRawInterviews] = useState([]);
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [opportunityOptions, setOpportunityOptions] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunityType, setOpportunityType] = useState('');
  const [postingId, setPostingId] = useState('');
  const [evaluationQuestions, setEvaluationQuestions] = useState([])
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
  const [formData, setFormData] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [evaluationMode, setEvaluationMode] = useState('general'); 
  const [strengths, setStrengths] = useState(['']);
  const [developmentAreas, setDevelopmentAreas] = useState(['']);
  const [finalComments, setFinalComments] = useState("");
  const [recommendations, setRecommendations] = useState(initialRecommendationState);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [existingGeneralEvalId, setExistingGeneralEvalId] = useState(null);
  const [existingParamEvalId, setExistingParamEvalId] = useState(null);

  const resetFormState = () => {
    setEvaluationQuestions([]);
    setFormData({});
    setStrengths(['']);
    setDevelopmentAreas(['']);
    setFinalComments('');
    setRecommendations(initialRecommendationState);
    setIsReadOnly(false);
    setExistingGeneralEvalId(null);
    setExistingParamEvalId(null);
  }

  const formatOpportunityType = (typeString) => {
    if (!typeString) return "Opportunity";
    return typeString.replace(/_/g, ' ');
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // Step 1: Fetch email from UserProfile API
        const storedEmail = localStorage.getItem("email");
        const profileResponse = await axios.post(`${API_BASE_URL}UserProfile/`, { email: storedEmail });
        const profileEmail = profileResponse.data?.user_data?.email || profileResponse.data?.email || storedEmail;

        // Step 2: Fetch interviews using the email from UserProfile
        const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/${profileEmail}/`);
        const allInterviews = Array.isArray(interviewsResponse.data) ? interviewsResponse.data : [interviewsResponse.data];
        setRawInterviews(allInterviews);
        
        if (allInterviews.length > 0) {
          const uniqueMap = new Map();
          allInterviews.forEach(interview => {
             if (interview.candidate_id && !uniqueMap.has(interview.candidate_id)) {
                 uniqueMap.set(interview.candidate_id, {
                     value: interview.candidate_id,
                     label: `${interview.candidate} (ID: ${interview.candidate_id})`,
                     candidate_name: interview.candidate,
                     candidate_email: interview.candidate_email
                 });
             }
          });
          setCandidateOptions(Array.from(uniqueMap.values()));
        }
      } catch (error) {
        Swal.fire("Error", `Failed to fetch interviews: ${error.message}`, "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    setOpportunityOptions([]);
    setSelectedOpportunity(null);
    setOpportunityType('');
    setPostingId('');
    resetFormState();

    if (selectedCandidate && rawInterviews.length > 0) {
        const candidateId = selectedCandidate.value;
        const specificInterviews = rawInterviews.filter(item => 
            item.candidate_id === candidateId
        );
        
        const opts = specificInterviews.map(interview => {
            const typeLabel = formatOpportunityType(interview.opening_post);
            const label = `${interview.job_title} (${typeLabel})`;
            return {
                value: interview.id,
                label: label,
                original_title: interview.job_title,
                opening_post: interview.opening_post,
                opening_post_id: interview.opening_post_id,
                ...interview
            };
        });
        setOpportunityOptions(opts);
    }
  }, [selectedCandidate, rawInterviews]);

useEffect(() => {
  if (!selectedOpportunity) {
    resetFormState();
    return;
  }

  const fetchQuestionsAndExistingData = async () => {
    setIsLoadingQuestions(true);
    resetFormState();
    
    const jobType = selectedOpportunity.opening_post;
    const interviewId = selectedOpportunity.value;
    const postingIdFromInterview = selectedOpportunity.opening_post_id;
    
    // Set display values
    setOpportunityType(formatOpportunityType(jobType));
    setPostingId(postingIdFromInterview || 'N/A');

    console.log("Selected Opportunity:", selectedOpportunity);
    console.log("Posting ID from interview:", postingIdFromInterview);
    console.log("Evaluation Mode:", evaluationMode);

    try {
      let rawQuestions = [];
      
      if (evaluationMode === 'general') {
        // For general mode - fetch template
        try {
          console.log("Fetching general template for job type:", jobType);
          const templateResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
          const allEvaluations = templateResponse.data || [];
          console.log("All templates:", allEvaluations);
          
          const template = allEvaluations.find(item => 
            item.candidate_name === "N/A - Template" && 
            item.job_type === jobType
          );

          console.log("Found template:", template);

          if (template && template.evaluation_details) {
            rawQuestions = template.evaluation_details.map((d, index) => ({
              id: `gen_${index}`,
              question_text: d.question_text || "General Question",
              area: "General Questions",
              answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
            }));
            console.log("General questions loaded:", rawQuestions);
          }
        } catch (error) {
          console.error("Error fetching general template:", error);
        }
      } else { 
        // For parameters mode
        if (postingIdFromInterview) {
          try {
            console.log(`Fetching parameters from: ${API_BASE_URL}job-postings/${postingIdFromInterview}/evaluation-parameters/`);
            const response = await axios.get(`${API_BASE_URL}job-postings/${postingIdFromInterview}/evaluation-parameters/`);
            const params = response.data || [];
            console.log("Parameters API response:", params);

            // Filter parameters to only include those that match the postingIdFromInterview
            const filteredParams = params.filter(p => 
              p.is_active !== false && 
              p.linked_posting_id === postingIdFromInterview
            );

            console.log("Filtered parameters for posting ID", postingIdFromInterview, ":", filteredParams);

            rawQuestions = filteredParams.map(p => {
              let areaLabel = p.area || "Specific Parameters";
              if (p.area === "BEHAVIORAL") areaLabel = "Additional Behaviour & Functional Parameters";
              else if (p.area === "ROLE_FIT") areaLabel = "Role-Specific & Cultural Fit";
              else if (p.area === "CORE_COMPETENCIES") areaLabel = "Core Competencies";

              return {
                id: `param_${p.id}`, 
                original_id: p.id,
                question_text: p.parameter_text || "Parameter",
                description: p.description || "", 
                area: areaLabel, 
                answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
              };
            });
            console.log("Processed questions:", rawQuestions);
          } catch (error) {
            console.error("Error fetching parameters:", error);
            console.error("Error details:", error.response?.data || error.message);
          }
        }
      }
      
      setEvaluationQuestions(rawQuestions);

      // Check for existing evaluations in BOTH modes
      try {
        let isReadOnlyMode = false;
        let newFormData = {};
        
        if (evaluationMode === 'general') {
          // Check for existing general evaluation
          console.log(`Checking for existing general evaluation for candidate: ${selectedCandidate.candidate_name}, job: ${postingIdFromInterview}`);
          const generalResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
          const allGeneralEvals = generalResponse.data || [];
          
          // Find evaluation for this candidate and posting ID
    const existingGeneralEval = allGeneralEvals.find(evaluation => 
      evaluation.candidate_name === selectedCandidate.candidate_name && 
      evaluation.job_posting?.toLowerCase() === postingIdFromInterview?.toLowerCase() &&
      evaluation.candidate_name !== "N/A - Template" // Exclude templates
    );

          console.log("Found existing general evaluation:", existingGeneralEval);

          if (existingGeneralEval) {
            isReadOnlyMode = true;
            setExistingGeneralEvalId(existingGeneralEval.id);
            
            // Load existing general evaluation data
            if (existingGeneralEval.evaluation_details) {
              existingGeneralEval.evaluation_details.forEach((detail, index) => {
                const ratingValue = RATING_SCALE_OPTIONS.find(opt => opt.numeric === detail.answer_value)?.value || "";
                newFormData[`question_gen_${index}_answer`] = ratingValue;
              });
            }
          }
        } else {
          // Check for existing parameter evaluation
          console.log(`Checking for existing parameter evaluation at: ${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`);
          try {
            const paramResponse = await axios.get(`${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`);
            if (paramResponse.data && Object.keys(paramResponse.data).length > 0) {
              isReadOnlyMode = true;
              setExistingParamEvalId(paramResponse.data.id);
              
              const existingData = paramResponse.data;
              
              // Load existing parameter evaluation data
              if (existingData.responses) {
                existingData.responses.forEach(resp => {
                  // Find the matching question to get the correct ID
                  const matchingQuestion = rawQuestions.find(q => q.original_id === (resp.parameter_id || resp.parameter));
                  if (matchingQuestion) {
                    const matchedOpt = RATING_SCALE_OPTIONS.find(o => o.numeric === resp.rating);
                    newFormData[`question_${matchingQuestion.id}_answer`] = matchedOpt ? matchedOpt.value : "";
                    newFormData[`question_${matchingQuestion.id}_comments`] = resp.comments || "";
                  }
                });
                
                // Set strengths, development areas, final comments
                if (existingData.strengths && Array.isArray(existingData.strengths)) {
                  setStrengths(existingData.strengths.length > 0 ? existingData.strengths : ['']);
                }
                if (existingData.development_areas && Array.isArray(existingData.development_areas)) {
                  setDevelopmentAreas(existingData.development_areas.length > 0 ? existingData.development_areas : ['']);
                }
                setFinalComments(existingData.final_comments || "");
                
                // Set recommendations
                if (existingData.overall_recommendation) {
                  const recState = { ...initialRecommendationState };
                  recState[existingData.overall_recommendation] = true;
                  setRecommendations(recState);
                }
              }
            }
          } catch (e) {
            console.log("No existing parameter evaluation found:", e.message);
          }
        }
        
        setIsReadOnly(isReadOnlyMode);
        if (isReadOnlyMode) {
          console.log("Loaded form data:", newFormData);
          setFormData(newFormData);
        }
      } catch (error) {
        console.error("Error checking existing evaluations:", error);
      }
    } catch (error) {
      console.error("Error in fetchQuestionsAndExistingData:", error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  
  fetchQuestionsAndExistingData();
}, [selectedOpportunity, evaluationMode]);

  const handleCandidateSelect = (option) => {
    setSelectedCandidate(option);
  };

  const handleOpportunitySelect = (option) => {
    setSelectedOpportunity(option);
    setEvaluationMode('general');
    if (option) {
        setOpportunityType(formatOpportunityType(option.opening_post));
        setPostingId(option.opening_post_id || 'N/A');
    } else {
        setOpportunityType('');
        setPostingId('');
    }
  };

const handleModeChange = (mode) => {
  // Always allow switching between modes to view data
  setEvaluationMode(mode);
  // Reset form data when switching modes
  setFormData({});
  // Reset readOnly flag - it will be set again in useEffect based on existing data
  setIsReadOnly(false);
  setStrengths(['']);
  setDevelopmentAreas(['']);
  setFinalComments('');
  setRecommendations(initialRecommendationState);
};

  const handleQuestionChange = (id, fieldType, value) => {
    if (isReadOnly) return;
    setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
  };

  const handleDynamicListChange = (setter, index, value) => {
    if (isReadOnly) return;
    setter(prevList => {
      const newList = [...prevList];
      newList[index] = value;
      return newList;
    });
  };
  
  const addDynamicListItem = (setter) => !isReadOnly && setter(prev => [...prev, '']);
  const removeDynamicListItem = (setter, index) => !isReadOnly && setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) {
      Swal.fire("Info", "This evaluation has already been submitted and cannot be modified.", "info");
      return;
    }
    
    if (!selectedCandidate || !selectedOpportunity) {
      Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let submissionUrl = "";
      let payload = {};
      const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
      const postingIdFromInterview = selectedOpportunity.opening_post_id;
      const jobType = selectedOpportunity.opening_post;

      if (evaluationMode === 'general') {
        // For general mode - use PUT if updating, POST if creating
        if (existingGeneralEvalId) {
          submissionUrl = `${API_BASE_URL}save_evaluation/${existingGeneralEvalId}/`;
          // PUT method for update
          payload = {
            candidate_name: selectedCandidate.candidate_name,
            candidate_email: selectedCandidate.candidate_email,
            interviewer_email: localStorage.getItem("email") || "unknown@example.com",
            job_type: jobType,
            job_posting_id: postingIdFromInterview,
            evaluation_details: evaluationQuestions.map(q => ({
              question_text: q.question_text,
              answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
            }))
          };
          await axios.put(submissionUrl, payload);
        } else {
          submissionUrl = `${API_BASE_URL}save_evaluation/`;
          payload = {
            candidate_name: selectedCandidate.candidate_name,
            candidate_email: selectedCandidate.candidate_email,
            interviewer_email: localStorage.getItem("email") || "unknown@example.com",
            job_type: jobType,
            job_posting_id: postingIdFromInterview,
            evaluation_details: evaluationQuestions.map(q => ({
              question_text: q.question_text,
              answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
            }))
          };
          await axios.post(submissionUrl, payload);
        }
      } else { 
        const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
        if (selectedRecs.length === 0) {
            Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
            setIsSubmitting(false);
            return;
        }
        const interviewId = selectedOpportunity.value; 
        
        // For parameter mode - use PUT if updating, POST if creating
        if (existingParamEvalId) {
          submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/${existingParamEvalId}/`;
          payload = {
            posting_type: jobType,
            posting_id: postingIdFromInterview,
            overall_recommendation: selectedRecs[0], 
            strengths: strengths.map(s => s.trim()).filter(s => s),
            development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
            final_comments: finalComments,
            responses: evaluationQuestions.map(q => ({
              parameter_id: q.original_id,
              rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
              comments: formData[`question_${q.id}_comments`] || "",
            })),
          };
          await axios.put(submissionUrl, payload);
        } else {
          submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`;
          payload = {
            posting_type: jobType,
            posting_id: postingIdFromInterview,
            overall_recommendation: selectedRecs[0], 
            strengths: strengths.map(s => s.trim()).filter(s => s),
            development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
            final_comments: finalComments,
            responses: evaluationQuestions.map(q => ({
              parameter_id: q.original_id,
              rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
              comments: formData[`question_${q.id}_comments`] || "",
            })),
          };
          await axios.post(submissionUrl, payload);
        }
      }
      
      console.log("Submitting payload:", payload);
      setIsReadOnly(true);
      Swal.fire("Success", `Evaluation ${existingGeneralEvalId || existingParamEvalId ? "updated" : "submitted"} successfully!`, "success");
    } catch (error) {
      console.error("Submission error:", error);
      const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
      Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

const renderTableFormControl = (question) => {
  const qId = question.id;
  const answerKey = `question_${qId}_answer`;
  const commentsKey = `question_${qId}_comments`;

  return {
    rating: (
      <Form.Control as="select" 
          value={formData[answerKey] || ""} 
          onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
          required 
          disabled={isReadOnly}
          size="sm">
        <option value="">-- Select Rating --</option>
        {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </Form.Control>
    ),
    comments: (
      <Form.Control as="textarea" 
          rows={2} 
          value={formData[commentsKey] || ""} 
          onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
          onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
          placeholder="Comments..." 
          disabled={isReadOnly}
          style={{ overflow: 'hidden', resize: 'none' }}
          size="sm"/>
    ),
  };
};

  const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
    const area = q.area || "General Questions";
    if (!groups[area]) groups[area] = [];
    groups[area].push(q);
    return groups;
  }, {});

  return (
    <>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <div className="main">
          <div className="container-fluid p-3 d-flex flex-column h-100">
            <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
              <div className="card-header bg-primary text-white">
                <h2 className="mb-0 h4">Candidate Interview Evaluation {isReadOnly && <span className="badge bg-warning text-dark ms-2">Submitted</span>}</h2>
              </div>
              <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="candidateSelection" className="mb-4">
                        <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
                        <Select options={candidateOptions} value={selectedCandidate} onChange={handleCandidateSelect} placeholder="Search..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable />
                      </Form.Group>
                    </Col>

                    {selectedCandidate && (
                      <Col md={6}>
                        <Form.Group controlId="opportunitySelection" className="mb-4">
                          <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
                          <Select options={opportunityOptions} value={selectedOpportunity} onChange={handleOpportunitySelect} placeholder="Select interview..." isDisabled={isSubmitting} isClearable />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                  
                  {selectedOpportunity && (
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="opportunityType" className="mb-4">
                          <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
                          <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group controlId="postingId" className="mb-4">
                          <Form.Label className="fw-bold">4. Posting ID:</Form.Label>
                          <Form.Control type="text" value={postingId} readOnly disabled className="bg-light" />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  {selectedOpportunity && (
                    <div className="text-center mb-4">
                      <ButtonGroup>
                        <Button 
                          variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} 
                          onClick={() => handleModeChange('general')} 
                          disabled={isLoadingQuestions || isSubmitting}>
                          Interview Questions
                        </Button>
                        <Button 
                          variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} 
                          onClick={() => handleModeChange('parameters')} 
                          disabled={isLoadingQuestions || isSubmitting}>
                          Post Interview Evaluation
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}

                  {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /></div>}

                  {!isLoadingQuestions && selectedOpportunity && (
                    <>
                      {evaluationQuestions.length > 0 ? (
                        Object.entries(groupedQuestions).map(([area, questions]) => (
                          <div key={area} className="mb-5">
                            <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
                            <Table striped bordered hover responsive className="align-middle">
                              <thead className="table-dark">
                                <tr>
                                  <th style={{ width: "5%" }} className="text-center">Sr. No</th>
                                  <th style={{ width: evaluationMode === 'parameters' ? "35%" : "60%" }}>Parameter</th>
                                  {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
                                  <th style={{ width: "15%" }}>Rating</th>
                                  <th style={{ width: "20%" }}>Comments</th>
                                </tr>
                              </thead>
                              <tbody>
                                {questions.map((q, qIndex) => {
                                  const controls = renderTableFormControl(q);
                                  return (
                                    <tr key={q.id}>
                                      <td className="text-center fw-bold">{qIndex + 1}</td>
                                      <td className="text-start">{q.question_text}</td>
                                      {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
                                      <td>{controls.rating}</td>
                                      <td>{controls.comments}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        ))
                      ) : (
                        <div className="alert alert-info text-center">
                          {evaluationMode === 'general' 
                            ? "No general evaluation questions found for this opportunity type." 
                            : `No evaluation parameters found for posting ID: ${postingId}. Please check if parameters are configured.`}
                        </div>
                      )}
                      
                      {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
                        <div className="p-4 border rounded bg-light mt-4 shadow-sm">
                            <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Label className="fw-bold">Strengths</Form.Label>
                                    {strengths.map((strength, index) => (
                                        <InputGroup key={index} className="mb-2">
                                            <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} disabled={isReadOnly} />
                                            {!isReadOnly && <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}><FontAwesomeIcon icon={faTrash} /></Button>}
                                        </InputGroup>
                                    ))}
                                    {!isReadOnly && <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}><FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength</Button>}
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Label className="fw-bold">Areas for Development</Form.Label>
                                     {developmentAreas.map((area, index) => (
                                        <InputGroup key={index} className="mb-2">
                                            <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} disabled={isReadOnly} />
                                            {!isReadOnly && <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}><FontAwesomeIcon icon={faTrash} /></Button>}
                                        </InputGroup>
                                    ))}
                                    {!isReadOnly && <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}><FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area</Button>}
                                </Col>
                            </Row>
                             <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Final Comments</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3} 
                                    value={finalComments} 
                                    onChange={e => setFinalComments(e.target.value)} 
                                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                    disabled={isReadOnly}
                                    style={{ overflow: 'hidden', resize: 'none' }}
                                />
                            </Form.Group>
                             <Form.Group>
                                <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex flex-wrap gap-3">
                                {RECOMMENDATION_OPTIONS.map(opt => (
                                    <Form.Check key={opt.value} type="radio" id={`rec-${opt.value}`} label={opt.label} name="recommendation" checked={recommendations[opt.value]} disabled={isReadOnly} onChange={() => setRecommendations({ ...initialRecommendationState, [opt.value]: true })} />
                                ))}
                                </div>
                            </Form.Group>
                        </div>
                      )}
                      
                      <div className="text-center mt-4 pb-5">
                        {evaluationQuestions.length > 0 && (
                          <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions}>
                            {isSubmitting ? <Spinner size="sm" animation="border" /> : isReadOnly ? "Update Evaluation" : "Submit Evaluation"}
                          </Button>
                        )}
                        {isReadOnly && <div className="alert alert-warning d-inline-block px-5 mt-3">This evaluation has already been submitted.</div>}
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   const [isLoading, setIsLoading] = useState(false);
//   const [rawInterviews, setRawInterviews] = useState([]);
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);
//   const [opportunityType, setOpportunityType] = useState('');
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general'); 
//   const [activePostingId, setActivePostingId] = useState(null);
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);
//   const [isReadOnly, setIsReadOnly] = useState(false);

//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//     setActivePostingId(null);
//     setIsReadOnly(false);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     return typeString.replace(/_/g, ' ');
//   };

//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
//           allInterviews.forEach(interview => {
//              if (interview.candidate_id && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id,
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch interviews: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   useEffect(() => {
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId
//         );
        
//         const opts = specificInterviews.map(interview => {
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;
//             return {
//                 value: interview.id,
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post,
//                 ...interview
//             };
//         });
//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews]);

//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestionsAndExistingData = async () => {
//       setIsLoadingQuestions(true);
//       const jobType = selectedOpportunity.opening_post;
//       const interviewId = selectedOpportunity.value;
      
//       let rawQuestions = [];
//       try {
//         const templateResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
//         const allEvaluations = templateResponse.data || [];

//         const template = allEvaluations.find(item => 
//             item.candidate_name === "N/A - Template" && 
//             item.job_type === jobType
//         );

//         if (!template || !template.job_posting) {
//             setIsLoadingQuestions(false);
//             return;
//         }

//         const derivedId = template.job_posting; 
//         setActivePostingId(derivedId);

//         let existingData = null;
//         try {
//             const checkRes = await axios.get(`${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`);
//             if (checkRes.data) {
//                 existingData = checkRes.data;
//                 setIsReadOnly(true);
//             }
//         } catch (e) {
//             setIsReadOnly(false);
//         }

//         if (evaluationMode === 'general') {
//           if (template.evaluation_details) {
//             rawQuestions = template.evaluation_details.map((d, index) => ({
//               id: `gen_${index}`,
//               question_text: d.question_text,
//               area: "General Questions",
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//             }));
//           } 
//         } else { 
//             const response = await axios.get(`${API_BASE_URL}job-postings/${derivedId}/evaluation-parameters/`);
//             const params = response.data || [];

//             rawQuestions = params.filter(p => {
//             if (!p.is_active) return false;
//             return p.job_posting === derivedId || p.business_opportunity === derivedId || p.technical_consultant === derivedId;
//             }).map(p => {
//                 let areaLabel = p.area || "Specific Parameters";
//                 if (p.area === "BEHAVIORAL") areaLabel = "Additional Behaviour & Functional Parameters";
//                 else if (p.area === "ROLE_FIT") areaLabel = "Role-Specific & Cultural Fit";
//                 else if (p.area === "CORE_COMPETENCIES") areaLabel = "Core Competencies";

//                 return {
//                     id: `param_${p.id}`, 
//                     original_id: p.id,
//                     question_text: p.parameter_text,
//                     description: p.description, 
//                     area: areaLabel, 
//                     answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//                 };
//             });
//         }
//         setEvaluationQuestions(rawQuestions);

//         if (existingData) {
//             const newFormData = {};
//             if (evaluationMode === 'parameters' && existingData.responses) {
//                 existingData.responses.forEach(resp => {
//                     const matchedOpt = RATING_SCALE_OPTIONS.find(o => o.numeric === resp.rating);
//                     newFormData[`question_param_${resp.parameter}_answer`] = matchedOpt ? matchedOpt.value : "";
//                     newFormData[`question_param_${resp.parameter}_comments`] = resp.comments || "";
//                 });
//                 setStrengths(existingData.strengths && existingData.strengths.length > 0 ? existingData.strengths : ['']);
//                 setDevelopmentAreas(existingData.development_areas && existingData.development_areas.length > 0 ? existingData.development_areas : ['']);
//                 setFinalComments(existingData.final_comments || "");
//                 if (existingData.overall_recommendation) {
//                     setRecommendations({ ...initialRecommendationState, [existingData.overall_recommendation]: true });
//                 }
//             }
//             setFormData(newFormData);
//         }
//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", "Could not load evaluation data.", "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestionsAndExistingData();
//   }, [selectedOpportunity, evaluationMode]);

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general');
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     if (isReadOnly) return;
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   const handleDynamicListChange = (setter, index, value) => {
//     if (isReadOnly) return;
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => !isReadOnly && setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => !isReadOnly && setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (isReadOnly) return;
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }

//     setIsSubmitting(true);
//     try {
//       let submissionUrl = "";
//       let payload = {};
//       const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}save_evaluation/`;
//         payload = {
//           candidate_name: selectedCandidate.candidate_name,
//           candidate_email: selectedCandidate.candidate_email,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           job_type: selectedOpportunity.opening_post,
//           job_posting_id: activePostingId,
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_text: q.question_text,
//             answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
//           }))
//         };
//       } else { 
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`;
//         payload = {
//           posting_type: selectedOpportunity.opening_post,
//           posting_id: activePostingId,
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }
//       await axios.post(submissionUrl, payload);
//       setIsReadOnly(true);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             disabled={isReadOnly}
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             disabled={isReadOnly}
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white">
//                 <h2 className="mb-0 h4">Candidate Interview Evaluation {isReadOnly && <span className="badge bg-warning text-dark ms-2">Submitted</span>}</h2>
//               </div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select options={candidateOptions} value={selectedCandidate} onChange={handleCandidateSelect} placeholder="Search..." isLoading={isLoading} isDisabled={isLoading || isSubmitting || isReadOnly} isClearable />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select options={opportunityOptions} value={selectedOpportunity} onChange={handleOpportunitySelect} placeholder="Select interview..." isLoading={isLoadingOpportunities} isDisabled={isLoadingOpportunities || isSubmitting || isReadOnly} isClearable />
//                       </Form.Group>
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /></div>}

//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td><div className="fw-bold">{q.question_text}</div></td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : <div className="alert alert-info text-center">No evaluation parameters found.</div>}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} disabled={isReadOnly} />
//                                             {!isReadOnly && <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}><FontAwesomeIcon icon={faTrash} /></Button>}
//                                         </InputGroup>
//                                     ))}
//                                     {!isReadOnly && <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}><FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength</Button>}
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} disabled={isReadOnly} />
//                                             {!isReadOnly && <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}><FontAwesomeIcon icon={faTrash} /></Button>}
//                                         </InputGroup>
//                                     ))}
//                                     {!isReadOnly && <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}><FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area</Button>}
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} disabled={isReadOnly}/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check key={opt.value} type="radio" id={`rec-${opt.value}`} label={opt.label} name="recommendation" checked={recommendations[opt.value]} disabled={isReadOnly} onChange={() => setRecommendations({ ...initialRecommendationState, [opt.value]: true })} />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         {!isReadOnly && (
//                           <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                             {isSubmitting ? <Spinner size="sm" animation="border" /> : "Submit Evaluation"}
//                           </Button>
//                         )}
//                         {isReadOnly && <div className="alert alert-warning d-inline-block px-5">This evaluation has already been submitted and cannot be modified.</div>}
//                       </div>
//                     </>
//                   )}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }



// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   const [isLoading, setIsLoading] = useState(false);
//   const [rawInterviews, setRawInterviews] = useState([]);
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);
//   const [opportunityType, setOpportunityType] = useState('');
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general'); 
//   const [activePostingId, setActivePostingId] = useState(null);
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);

//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//     setActivePostingId(null);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     return typeString.replace(/_/g, ' ');
//   };

//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
//           allInterviews.forEach(interview => {
//              if (interview.candidate_id && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id,
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch interviews: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   useEffect(() => {
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId
//         );
        
//         const opts = specificInterviews.map(interview => {
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;
//             return {
//                 value: interview.id,
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post,
//                 ...interview
//             };
//         });
//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews]);

//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       const jobType = selectedOpportunity.opening_post;
      
//       let rawQuestions = [];
//       try {
//         const templateResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
//         const allEvaluations = templateResponse.data || [];

//         const template = allEvaluations.find(item => 
//             item.candidate_name === "N/A - Template" && 
//             item.job_type === jobType
//         );

//         if (!template || !template.job_posting) {
//             if (evaluationMode === 'parameters') {
//                 Swal.fire("Error", "No Template or Posting ID found for this Job Type in save_evaluation.", "error");
//             }
//             setIsLoadingQuestions(false);
//             return;
//         }

//         const derivedId = template.job_posting; 
//         setActivePostingId(derivedId);

//         if (evaluationMode === 'general') {
//           if (template.evaluation_details) {
//             rawQuestions = template.evaluation_details.map((d, index) => ({
//               id: `gen_${index}`,
//               question_text: d.question_text,
//               area: "General Questions",
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//             }));
//           } 
//         } else { 
//           if (!derivedId) {
//              Swal.fire("Error", "No Posting ID found in template.", "error");
//           } else {
//               const response = await axios.get(`${API_BASE_URL}job-postings/${derivedId}/evaluation-parameters/`);
//               const params = response.data || [];

//               rawQuestions = params.filter(p => {
//                 if (!p.is_active) return false;
//                 const matchesJob = p.job_posting === derivedId;
//                 const matchesBusiness = p.business_opportunity === derivedId;
//                 const matchesConsultant = p.technical_consultant === derivedId;
//                 return matchesJob || matchesBusiness || matchesConsultant;
//               }).map(p => {
//                   let areaLabel = p.area || "Specific Parameters";
//                   if (p.area === "BEHAVIORAL") {
//                       areaLabel = "Additional Behaviour & Functional Parameters";
//                   } else if (p.area === "ROLE_FIT") {
//                       areaLabel = "Role-Specific & Cultural Fit";
//                   } else if (p.area === "CORE_COMPETENCIES") {
//                       areaLabel = "Core Competencies";
//                   }

//                   return {
//                       id: `param_${p.id}`, 
//                       original_id: p.id,
//                       question_text: p.parameter_text,
//                       description: p.description, 
//                       area: areaLabel, 
//                       answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//                   };
//               });
//           }
//         }
//         setEvaluationQuestions(rawQuestions);
//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", "Could not load evaluation parameters.", "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedOpportunity, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ 
//         ...acc, 
//         [`question_${q.id}_answer`]: "", 
//         [`question_${q.id}_comments`]: "" 
//     }), {}));
//   }, [evaluationQuestions]);

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general');
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }
//     if (!activePostingId) {
//         Swal.fire("Error", "Could not determine Posting ID. Please try refreshing or re-selecting.", "error");
//         return;
//     }

//     setIsSubmitting(true);
//     try {
//       let submissionUrl = "";
//       let payload = {};
//       const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}save_evaluation/`;
//         payload = {
//           candidate_name: selectedCandidate.candidate_name,
//           candidate_email: selectedCandidate.candidate_email,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           job_type: selectedOpportunity.opening_post,
//           job_posting_id: activePostingId,
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_text: q.question_text,
//             answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
//           }))
//         };
//       } else { 
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`;
//         payload = {
//           posting_type: selectedOpportunity.opening_post,
//           posting_id: activePostingId,
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }
//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select 
//                         options={candidateOptions} 
//                         value={selectedCandidate} 
//                         onChange={handleCandidateSelect} 
//                         placeholder="Search by name or ID..." 
//                         isLoading={isLoading} 
//                         isDisabled={isLoading || isSubmitting} 
//                         isClearable
//                     />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select 
//                             options={opportunityOptions} 
//                             value={selectedOpportunity} 
//                             onChange={handleOpportunitySelect} 
//                             placeholder={isLoadingOpportunities ? "Loading..." : "Select the interview opportunity..."} 
//                             isLoading={isLoadingOpportunities} 
//                             isDisabled={isLoadingOpportunities || isSubmitting} 
//                             isClearable 
//                             noOptionsMessage={() => "No scheduled interviews found for this candidate"}
//                         />
//                       </Form.Group>
                      
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading questions...</p></div>}

//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded shadow-sm border">
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Candidate:</strong> {selectedCandidate?.candidate_name}</p>
//                             <p className="mb-0"><strong>Role:</strong> {selectedOpportunity?.label}</p>
//                         </div>
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
//                             <p className="mb-0"><strong>Interviewer:</strong> {localStorage.getItem("email") || "Current User"}</p>
//                         </div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td>
//                                         <div className="fw-bold">{q.question_text}</div>
//                                       </td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info text-center">
//                             No active evaluation parameters found matching ID: <strong>{activePostingId || "Unknown"}</strong>
//                         </div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts on the candidate..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check 
//                                         key={opt.value} 
//                                         type="radio" 
//                                         id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name="recommendation"
//                                         checked={recommendations[opt.value]}
//                                         onChange={() => {
//                                             const newState = { ...initialRecommendationState, [opt.value]: true };
//                                             setRecommendations(newState);
//                                         }}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" animation="border" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedCandidate && !isLoading && <div className="text-center p-5 text-muted border rounded bg-light mt-3"><p className="mb-0">Please select a candidate to begin the evaluation.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
    
//   )
// }



// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // --- State Management ---
//   const [isLoading, setIsLoading] = useState(false);
  
//   // Data Stores
//   const [rawInterviews, setRawInterviews] = useState([]);
  
//   // Step 1: Candidate Selection
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);

//   // Step 2: Opportunity Selection
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

//   // Step 3: Type (Auto-filled)
//   const [opportunityType, setOpportunityType] = useState('');

//   // Evaluation Form State
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general'); // 'general' or 'parameters'

//   // ID derived from Template (e.g. "job-031")
//   const [activePostingId, setActivePostingId] = useState(null);

//   // Dynamic Summary State (For Parameters Mode)
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);

//   // --- Helpers ---
//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//     setActivePostingId(null);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     return typeString.replace(/_/g, ' ');
//   };

//   // --- Effects ---

//   // 1. Initial Data Fetch (Interviews)
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         // Process Candidates (Unique List)
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
          
//           allInterviews.forEach(interview => {
//              // Ensure we have a valid candidate
//              if (interview.candidate_id && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id,
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }

//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch interviews: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // 2. Handle Candidate Selection -> Populate Opportunities
//   useEffect(() => {
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
        
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId
//         );
        
//         const opts = specificInterviews.map(interview => {
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;

//             return {
//                 value: interview.id, // Interview ID
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post, // Type
//                 // We no longer rely on opening_post_id from here for the FETCH, 
//                 // but we keep the object intact.
//                 ...interview
//             };
//         });

//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews]);

//   // 3. Fetch Questions when Opportunity & Mode Selected (UPDATED LOGIC)
//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       const jobType = selectedOpportunity.opening_post; // e.g. "Job_Opening"
      
//       let rawQuestions = [];
//       try {
//         // --- STEP 1: Fetch Templates to find the correct ID (job-031 etc) ---
//         const templateResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
//         const allEvaluations = templateResponse.data || [];

//         // Find the template where candidate is "N/A - Template" and matches current jobType
//         const template = allEvaluations.find(item => 
//             item.candidate_name === "N/A - Template" && 
//             item.job_type === jobType
//         );

//         if (!template || !template.job_posting) {
//             console.warn("No template found matching this job type.");
//             // Stop here if we can't find the reference ID
//             if (evaluationMode === 'parameters') {
//                 Swal.fire("Error", "No Template or Posting ID found for this Job Type in save_evaluation.", "error");
//             }
//             setIsLoadingQuestions(false);
//             return;
//         }

//         // Extract the ID to be used for fetching parameters (e.g., "job-031")
//         const derivedId = template.job_posting; 
//         setActivePostingId(derivedId); // Save for submission

//         if (evaluationMode === 'general') {
//           // --- General Questions (From the Template itself) ---
//           if (template.evaluation_details) {
//             rawQuestions = template.evaluation_details.map((d, index) => ({
//               id: `gen_${index}`,
//               question_text: d.question_text,
//               area: "General Questions",
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//             }));
//           } 

//         } else { 
//           // --- Other Parameters (Fetch using the derived ID) ---
//           if (!derivedId) {
//              Swal.fire("Error", "No Posting ID (e.g., business-xxx) found in template.", "error");
//           } else {
//               // Fetch parameters using the ID from the template
//               const response = await axios.get(`${API_BASE_URL}job-postings/${derivedId}/evaluation-parameters/`);
//               const params = response.data || [];

//               // FILTER LOGIC: Strict Match against keys
//               rawQuestions = params.filter(p => {
//                 // 1. Must be active
//                 if (!p.is_active) return false;

//                 // 2. ID Match Logic
//                 // Match against the ID we derived from the template
//                 const matchesJob = p.job_posting === derivedId;
//                 const matchesBusiness = p.business_opportunity === derivedId;
//                 const matchesConsultant = p.technical_consultant === derivedId;

//                 return matchesJob || matchesBusiness || matchesConsultant;
//               }).map(p => ({
//                   id: `param_${p.id}`, 
//                   original_id: p.id,
//                   question_text: p.parameter_text,
//                   description: p.description, 
//                   area: p.area || "Specific Parameters", 
//                   answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//               }));
//           }
//         }

//         setEvaluationQuestions(rawQuestions);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           console.error("Error fetching questions:", error);
//           Swal.fire("Error", "Could not load evaluation parameters.", "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedOpportunity, evaluationMode]);

//   // Initialize Form Data
//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ 
//         ...acc, 
//         [`question_${q.id}_answer`]: "", 
//         [`question_${q.id}_comments`]: "" 
//     }), {}));
//   }, [evaluationQuestions]);


//   // --- Handlers ---

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general'); // Reset to general
    
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }
    
//     // Ensure we have the posting ID (fetched from template logic)
//     if (!activePostingId) {
//         Swal.fire("Error", "Could not determine Posting ID. Please try refreshing or re-selecting.", "error");
//         return;
//     }

//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};
//       const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}save_evaluation/`;
//         payload = {
//           candidate_name: selectedCandidate.candidate_name,
//           candidate_email: selectedCandidate.candidate_email,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           job_type: selectedOpportunity.opening_post,
//           job_posting_id: activePostingId, // Use the ID derived from template
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_text: q.question_text,
//             answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
//           }))
//         };
//       } else { 
//         // Parameters Mode Submission
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`;
        
//         payload = {
//           posting_type: selectedOpportunity.opening_post,
//           posting_id: activePostingId, // Use the ID derived from template
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       console.log("Submitting payload:", payload);
//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       console.error("Submission Error", error);
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
                  
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select 
//                         options={candidateOptions} 
//                         value={selectedCandidate} 
//                         onChange={handleCandidateSelect} 
//                         placeholder="Search by name or ID..." 
//                         isLoading={isLoading} 
//                         isDisabled={isLoading || isSubmitting} 
//                         isClearable
//                     />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select 
//                             options={opportunityOptions} 
//                             value={selectedOpportunity} 
//                             onChange={handleOpportunitySelect} 
//                             placeholder={isLoadingOpportunities ? "Loading..." : "Select the interview opportunity..."} 
//                             isLoading={isLoadingOpportunities} 
//                             isDisabled={isLoadingOpportunities || isSubmitting} 
//                             isClearable 
//                             noOptionsMessage={() => "No scheduled interviews found for this candidate"}
//                         />
//                       </Form.Group>
                      
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading questions...</p></div>}

//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded shadow-sm border">
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Candidate:</strong> {selectedCandidate?.candidate_name}</p>
//                             <p className="mb-0"><strong>Role:</strong> {selectedOpportunity?.label}</p>
//                         </div>
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
//                             <p className="mb-0"><strong>Interviewer:</strong> {localStorage.getItem("email") || "Current User"}</p>
//                         </div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td>
//                                         <div className="fw-bold">{q.question_text}</div>
//                                       </td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info text-center">
//                              {/* Display activePostingId if available to confirm what triggered the search */}
//                             No active evaluation parameters found matching ID: <strong>{activePostingId || "Unknown"}</strong>
//                         </div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts on the candidate..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check 
//                                         key={opt.value} 
//                                         type="radio" 
//                                         id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name="recommendation"
//                                         checked={recommendations[opt.value]}
//                                         onChange={() => {
//                                             const newState = { ...initialRecommendationState, [opt.value]: true };
//                                             setRecommendations(newState);
//                                         }}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" animation="border" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedCandidate && !isLoading && <div className="text-center p-5 text-muted border rounded bg-light mt-3"><p className="mb-0">Please select a candidate to begin the evaluation.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }


// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // --- State Management ---
//   const [isLoading, setIsLoading] = useState(false);
  
//   // Data Stores
//   const [rawInterviews, setRawInterviews] = useState([]);
  
//   // Step 1: Candidate Selection
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);

//   // Step 2: Opportunity Selection
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

//   // Step 3: Type (Auto-filled)
//   const [opportunityType, setOpportunityType] = useState('');

//   // Evaluation Form State
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general'); // 'general' or 'parameters'

//   // Dynamic Summary State (For Parameters Mode)
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);

//   // --- Helpers ---
//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     return typeString.replace(/_/g, ' ');
//   };

//   // --- Effects ---

//   // 1. Initial Data Fetch (Interviews)
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         // Process Candidates (Unique List)
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
          
//           allInterviews.forEach(interview => {
//              // Ensure we have a valid candidate
//              if (interview.candidate_id && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id,
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }

//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch interviews: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // 2. Handle Candidate Selection -> Populate Opportunities
//   useEffect(() => {
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
        
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId
//         );
        
//         const opts = specificInterviews.map(interview => {
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;

//             // Extract the critical ID for matching (e.g., "business-029", "consultant-023")
//             const effectiveId = interview.opening_post_id || interview.job_posting_id;

//             return {
//                 value: interview.id, // Interview ID
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post, // Type
//                 opening_post_id: effectiveId,         // THE ID USED FOR MATCHING
//                 ...interview
//             };
//         });

//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews]);

//   // 3. Fetch Questions when Opportunity & Mode Selected
//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       const jobType = selectedOpportunity.opening_post; // e.g. "Business_Opportunity"
//       const specificId = selectedOpportunity.opening_post_id; // e.g. "business-029"

//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           // --- General Questions ---
//           const response = await axios.get(`${API_BASE_URL}save_evaluation/`);
//           const allEvaluations = response.data;

//           const template = allEvaluations.find(item => 
//             item.candidate_name === "N/A - Template" && 
//             item.job_type === jobType
//           );

//           if (template && template.evaluation_details) {
//             rawQuestions = template.evaluation_details.map((d, index) => ({
//               id: `gen_${index}`,
//               question_text: d.question_text,
//               area: "General Questions",
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//             }));
//           } else {
//              console.warn("No template found for this job type in save_evaluation history.");
//           }

//         } else { 
//           // --- Other Parameters (With Specific Filtering) ---
//           if (!specificId) {
//              Swal.fire("Error", "No Posting ID (e.g., business-xxx) found for this interview.", "error");
//           } else {
//               // Fetch parameters (Assuming this endpoint returns a list that needs filtering)
//               const response = await axios.get(`${API_BASE_URL}job-postings/${specificId}/evaluation-parameters/`);
//               const params = response.data || [];

//               // FILTER LOGIC: Strict Match against keys
//               rawQuestions = params.filter(p => {
//                 // 1. Must be active
//                 if (!p.is_active) return false;

//                 // 2. ID Match Logic
//                 // If the opening_post_id matches ANY of the three keys, include it.
//                 const matchesJob = p.job_posting === specificId;
//                 const matchesBusiness = p.business_opportunity === specificId;
//                 const matchesConsultant = p.technical_consultant === specificId;

//                 return matchesJob || matchesBusiness || matchesConsultant;
//               }).map(p => ({
//                   id: `param_${p.id}`, 
//                   original_id: p.id,
//                   question_text: p.parameter_text,
//                   description: p.description, 
//                   area: p.area || "Specific Parameters", 
//                   answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//               }));
//           }
//         }

//         setEvaluationQuestions(rawQuestions);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           console.error("Error fetching questions:", error);
//           Swal.fire("Error", "Could not load evaluation parameters.", "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedOpportunity, evaluationMode]);

//   // Initialize Form Data
//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ 
//         ...acc, 
//         [`question_${q.id}_answer`]: "", 
//         [`question_${q.id}_comments`]: "" 
//     }), {}));
//   }, [evaluationQuestions]);


//   // --- Handlers ---

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general'); // Reset to general
    
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};
//       const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}save_evaluation/`;
//         payload = {
//           candidate_name: selectedCandidate.candidate_name,
//           candidate_email: selectedCandidate.candidate_email,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           job_type: selectedOpportunity.opening_post,
//           job_posting_id: selectedOpportunity.opening_post_id,
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_text: q.question_text,
//             answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
//           }))
//         };
//       } else { 
//         // Parameters Mode Submission
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`;
        
//         payload = {
//           posting_type: selectedOpportunity.opening_post,
//           posting_id: selectedOpportunity.opening_post_id,
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       console.log("Submitting payload:", payload);
//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       console.error("Submission Error", error);
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
                  
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select 
//                         options={candidateOptions} 
//                         value={selectedCandidate} 
//                         onChange={handleCandidateSelect} 
//                         placeholder="Search by name or ID..." 
//                         isLoading={isLoading} 
//                         isDisabled={isLoading || isSubmitting} 
//                         isClearable
//                     />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select 
//                             options={opportunityOptions} 
//                             value={selectedOpportunity} 
//                             onChange={handleOpportunitySelect} 
//                             placeholder={isLoadingOpportunities ? "Loading..." : "Select the interview opportunity..."} 
//                             isLoading={isLoadingOpportunities} 
//                             isDisabled={isLoadingOpportunities || isSubmitting} 
//                             isClearable 
//                             noOptionsMessage={() => "No scheduled interviews found for this candidate"}
//                         />
//                       </Form.Group>
                      
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading questions...</p></div>}

//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded shadow-sm border">
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Candidate:</strong> {selectedCandidate?.candidate_name}</p>
//                             <p className="mb-0"><strong>Role:</strong> {selectedOpportunity?.label}</p>
//                         </div>
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
//                             <p className="mb-0"><strong>Interviewer:</strong> {localStorage.getItem("email") || "Current User"}</p>
//                         </div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td>
//                                         <div className="fw-bold">{q.question_text}</div>
//                                       </td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info text-center">
//                             No active evaluation parameters found matching ID: <strong>{selectedOpportunity.opening_post_id}</strong>
//                         </div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts on the candidate..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check 
//                                         key={opt.value} 
//                                         type="radio" 
//                                         id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name="recommendation"
//                                         checked={recommendations[opt.value]}
//                                         onChange={() => {
//                                             const newState = { ...initialRecommendationState, [opt.value]: true };
//                                             setRecommendations(newState);
//                                         }}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" animation="border" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedCandidate && !isLoading && <div className="text-center p-5 text-muted border rounded bg-light mt-3"><p className="mb-0">Please select a candidate to begin the evaluation.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }

// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // --- State Management ---
//   const [isLoading, setIsLoading] = useState(false);
  
//   // Data Stores
//   const [jobPostingMap, setJobPostingMap] = useState({}); // Mapping of job titles/types to posting IDs
//   const [rawInterviews, setRawInterviews] = useState([]);
  
//   // Step 1: Candidate Selection
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);

//   // Step 2: Opportunity Selection
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

//   // Step 3: Type (Auto-filled)
//   const [opportunityType, setOpportunityType] = useState('');

//   // Evaluation Form State
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // Dynamic Summary State
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   // --- Helpers ---
//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     return typeString.replace(/_/g, ' ');
//   };

//   // Build mapping from job postings API
//   const buildJobPostingMap = (jobsData) => {
//     const map = {};
    
//     // Map Jobs
//     if (jobsData.jobpostings) {
//       jobsData.jobpostings.forEach(job => {
//         map[job.jobtitle] = job.job_id; // e.g., "Senior Python Developer" -> "job-067"
//       });
//     }
    
//     // Map Business Opportunities
//     if (jobsData.business_opportunities) {
//       jobsData.business_opportunities.forEach(biz => {
//         map[biz.business_title] = biz.business_id; // e.g., "LN Business Testing" -> "business-027"
//       });
//     }
    
//     // Map Technical Consultants
//     if (jobsData.technical_consultants) {
//       jobsData.technical_consultants.forEach(cons => {
//         map[cons.title] = cons.consultant_id; // e.g., "LN Cons Testing" -> "consultant-023"
//       });
//     }
    
//     return map;
//   };

//   // --- Effects ---

//   // 1. Initial Data Fetch
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         // Fetch job postings to build mapping
//         const jobPostingsResponse = await axios.get(`${API_BASE_URL}JobPostingresultByStatus/`);
//         const postingMap = buildJobPostingMap(jobPostingsResponse.data);
//         setJobPostingMap(postingMap);

//         // Fetch All Scheduled Interviews
//         const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         // Process Candidates (Unique List)
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
          
//           allInterviews.forEach(interview => {
//              // Only include if 'opening_post' exists and candidate is unique
//              if (interview.opening_post && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id,
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }

//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // 2. Handle Candidate Selection -> Populate Opportunities
//   useEffect(() => {
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
        
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId && item.opening_post
//         );
        
//         // Extract job_posting_id from mapping
//         const opts = specificInterviews.map(interview => {
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;

//             // Get posting ID from map
//             const postingId = jobPostingMap[interview.job_title];

//             return {
//                 value: interview.id,
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post,
//                 job_posting_id: postingId, 
//                 ...interview
//             };
//         });

//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews, jobPostingMap]);

//   // 3. Fetch Questions when Opportunity Selected
//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       const jobType = selectedOpportunity.opening_post;
//       const jobPostingId = selectedOpportunity.job_posting_id;

//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           // Fetch General Questions from save_evaluation endpoint
//           const response = await axios.get(`${API_BASE_URL}/save_evaluation/`);
//           const allEvaluations = response.data;

//           const template = allEvaluations.find(item => 
//             item.candidate_name === "N/A - Template" && 
//             item.job_type === jobType
//           );

//           if (template && template.evaluation_details) {
//             rawQuestions = template.evaluation_details.map((d, index) => ({
//               id: index,
//               question_text: d.question_text,
//               area: "General Questions",
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//             }));
//           } else {
//              console.warn("No template found for this job type in save_evaluation history.");
//           }

//         } else { 
//           // 'parameters' mode (UPDATED to use interview-evaluations endpoint)
//           if (!jobPostingId) {
//              console.warn("No Job Posting ID found for parameters lookup.");
//           } else {
//               // <CHANGE> Updated Endpoint
//               const response = await axios.get(`${API_BASE_URL}interview-evaluations/`);
//               // <CHANGE> Accessing results array from pagination response
//               const allEvaluations = response.data.results || [];

//               // <CHANGE> Filter for "N/A - Template" and matching job_posting_id
//               const template = allEvaluations.find(item => 
//                 item.candidate_name === "N/A - Template" && 
//                 item.job_posting === jobPostingId
//               );

//               if (template && template.evaluation_details) {
//                 rawQuestions = template.evaluation_details.map((d, index) => ({
//                   id: `param_${index}`, 
//                   original_id: index, // Note: New API doesn't provide ID, using index as fallback
//                   question_text: d.question_text,
//                   description: "Evaluation Parameter", // Default description
//                   area: "Technical/Specific Parameters", 
//                   answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//                   is_active: true,
//                 }));
//               } else {
//                 console.warn("No template found for this job posting in interview-evaluations.");
//               }
//           }
//         }

//         const uniqueQuestions = Array.from(new Map(rawQuestions.map(q => [q.id || q.question_text, q])).values());
        
//         const questionsToDisplay = evaluationMode === 'parameters' 
//             ? uniqueQuestions.filter(q => q.is_active === true) 
//             : uniqueQuestions;

//         setEvaluationQuestions(questionsToDisplay);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           console.error("Error fetching questions:", error);
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedOpportunity, evaluationMode]);

//   // Initialize Form Data keys
//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ 
//         ...acc, 
//         [`question_${q.id}_answer`]: "", 
//         [`question_${q.id}_comments`]: "" 
//     }), {}));
//   }, [evaluationQuestions]);


//   // --- Handlers ---

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general');
    
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         // Submit General Questions to save_evaluation
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
        
//         const getNumericRating = (ratingValue) => {
//              const option = RATING_SCALE_OPTIONS.find(opt => opt.value === ratingValue);
//              return option ? option.numeric : null;
//         };

//         payload = {
//           candidate_name: selectedCandidate.candidate_name,
//           candidate_email: selectedCandidate.candidate_email,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           job_type: selectedOpportunity.opening_post,
//           job_posting_id: selectedOpportunity.job_posting_id, 
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_text: q.question_text,
//             answer_value: getNumericRating(formData[`question_${q.id}_answer`])
//           }))
//         };

//       } else { 
//         // 'parameters' mode submission
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
        
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id, // Be aware: this is now an index based ID
//             parameter_text: q.question_text, // Sending text as backup if needed by backend
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       console.log("[v1] Submitting payload:", payload);
//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
      
//       setSelectedOpportunity(null);
//       setOpportunityType('');

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
                  
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select 
//                         options={candidateOptions} 
//                         value={selectedCandidate} 
//                         onChange={handleCandidateSelect} 
//                         placeholder="Search by name or ID..." 
//                         isLoading={isLoading} 
//                         isDisabled={isLoading || isSubmitting} 
//                         isClearable
//                     />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select 
//                             options={opportunityOptions} 
//                             value={selectedOpportunity} 
//                             onChange={handleOpportunitySelect} 
//                             placeholder={isLoadingOpportunities ? "Loading..." : "Select the interview opportunity..."} 
//                             isLoading={isLoadingOpportunities} 
//                             isDisabled={isLoadingOpportunities || isSubmitting} 
//                             isClearable 
//                             noOptionsMessage={() => "No scheduled interviews found for this candidate"}
//                         />
//                       </Form.Group>
                      
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading questions...</p></div>}

//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded shadow-sm border">
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Candidate:</strong> {selectedCandidate?.candidate_name}</p>
//                             <p className="mb-0"><strong>Role:</strong> {selectedOpportunity?.label}</p>
//                         </div>
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
//                             <p className="mb-0"><strong>Interviewer:</strong> {localStorage.getItem("email")}</p>
//                         </div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td>
//                                         <div className="fw-bold">{q.question_text}</div>
//                                       </td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info text-center">
//                             No {evaluationMode === 'general' ? 'general questions' : 'active evaluation parameters'} are configured for this role.
//                         </div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts on the candidate..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check 
//                                         key={opt.value} 
//                                         type="radio" 
//                                         id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name="recommendation"
//                                         checked={recommendations[opt.value]}
//                                         onChange={() => {
//                                             const newState = { ...initialRecommendationState, [opt.value]: true };
//                                             setRecommendations(newState);
//                                         }}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" animation="border" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedCandidate && !isLoading && <div className="text-center p-5 text-muted border rounded bg-light mt-3"><p className="mb-0">Please select a candidate to begin the evaluation.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }


// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // --- State Management ---
//   const [isLoading, setIsLoading] = useState(false);
  
//   // Data Stores
//   const [jobPostingMap, setJobPostingMap] = useState({}); // Mapping of job titles/types to posting IDs
//   const [rawInterviews, setRawInterviews] = useState([]);
  
//   // Step 1: Candidate Selection
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);

//   // Step 2: Opportunity Selection
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

//   // Step 3: Type (Auto-filled)
//   const [opportunityType, setOpportunityType] = useState('');

//   // Evaluation Form State
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // Dynamic Summary State
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   // --- Helpers ---
//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     return typeString.replace(/_/g, ' ');
//   };

//   // <CHANGE> Build mapping from job postings API
//   const buildJobPostingMap = (jobsData) => {
//     const map = {};
    
//     // Map Jobs
//     if (jobsData.jobpostings) {
//       jobsData.jobpostings.forEach(job => {
//         map[job.jobtitle] = job.job_id; // e.g., "Senior Python Developer" -> "job-067"
//       });
//     }
    
//     // Map Business Opportunities
//     if (jobsData.business_opportunities) {
//       jobsData.business_opportunities.forEach(biz => {
//         map[biz.business_title] = biz.business_id; // e.g., "LN Business Testing" -> "business-027"
//       });
//     }
    
//     // Map Technical Consultants
//     if (jobsData.technical_consultants) {
//       jobsData.technical_consultants.forEach(cons => {
//         map[cons.title] = cons.consultant_id; // e.g., "LN Cons Testing" -> "consultant-023"
//       });
//     }
    
//     return map;
//   };

//   // --- Effects ---

//   // 1. Initial Data Fetch
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         // <CHANGE> Fetch job postings to build mapping
//         const jobPostingsResponse = await axios.get(`${API_BASE_URL}JobPostingresultByStatus/`);
//         const postingMap = buildJobPostingMap(jobPostingsResponse.data);
//         setJobPostingMap(postingMap);

//         // Fetch All Scheduled Interviews
//         const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         // Process Candidates (Unique List)
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
          
//           allInterviews.forEach(interview => {
//              // Only include if 'opening_post' exists and candidate is unique
//              if (interview.opening_post && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id,
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }

//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // 2. Handle Candidate Selection -> Populate Opportunities
//   useEffect(() => {
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
        
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId && item.opening_post
//         );
        
//         // <CHANGE> Extract job_posting_id from mapping
//         const opts = specificInterviews.map(interview => {
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;

//             // Get posting ID from map - this is the key fix!
//             const postingId = jobPostingMap[interview.job_title];

//             return {
//                 value: interview.id,
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post,
//                 job_posting_id: postingId, // <CHANGE> This is the correct posting ID
//                 ...interview
//             };
//         });

//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews, jobPostingMap]);

//   // 3. Fetch Questions when Opportunity Selected
//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       const jobTitle = selectedOpportunity.original_title;
//       const jobType = selectedOpportunity.opening_post;

//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           // Fetch General Questions from save_evaluation endpoint
//           const response = await axios.get(`${API_BASE_URL}/save_evaluation/`);
//           const allEvaluations = response.data;

//           const template = allEvaluations.find(item => 
//             item.candidate_name === "N/A - Template" && 
//             item.job_type === jobType
//           );

//           if (template && template.evaluation_details) {
//             rawQuestions = template.evaluation_details.map((d, index) => ({
//               id: index,
//               question_text: d.question_text,
//               area: "General Questions",
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
//             }));
//           } else {
//              console.warn("No template found for this job type in save_evaluation history.");
//           }

//         } else { 
//           // 'parameters' mode (unchanged)
//           if (!selectedOpportunity.job_posting_id) {
//              console.warn("No Job Posting ID found for parameters lookup.");
//           } else {
//               const paramsUrl = `${API_BASE_URL}/job-postings/${selectedOpportunity.job_posting_id}/evaluation-parameters/`;
//               const response = await axios.get(paramsUrl);
//               if (response.data?.length > 0) {
//                 rawQuestions = response.data.map(p => ({
//                   id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//                   description: p.description, area: p.area_display,
//                   answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//                   is_active: p.is_active,
//                 }));
//               }
//           }
//         }

//         const uniqueQuestions = Array.from(new Map(rawQuestions.map(q => [q.id || q.question_text, q])).values());
        
//         const questionsToDisplay = evaluationMode === 'parameters' 
//             ? uniqueQuestions.filter(q => q.is_active === true) 
//             : uniqueQuestions;

//         setEvaluationQuestions(questionsToDisplay);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           console.error("Error fetching questions:", error);
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedOpportunity, evaluationMode]);

//   // Initialize Form Data keys
//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ 
//         ...acc, 
//         [`question_${q.id}_answer`]: "", 
//         [`question_${q.id}_comments`]: "" 
//     }), {}));
//   }, [evaluationQuestions]);


//   // --- Handlers ---

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general');
    
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         // Submit General Questions to save_evaluation
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
        
//         const getNumericRating = (ratingValue) => {
//              const option = RATING_SCALE_OPTIONS.find(opt => opt.value === ratingValue);
//              return option ? option.numeric : null;
//         };

//         // <CHANGE> Use the correct job_posting_id from selectedOpportunity
//         payload = {
//           candidate_name: selectedCandidate.candidate_name,
//           candidate_email: selectedCandidate.candidate_email,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           job_type: selectedOpportunity.opening_post,
//           job_posting_id: selectedOpportunity.job_posting_id, // e.g., "business-027"
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_text: q.question_text,
//             answer_value: getNumericRating(formData[`question_${q.id}_answer`])
//           }))
//         };

//       } else { 
//         // 'parameters' mode submission
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
        
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       console.log("[v0] Submitting payload:", payload);
//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
      
//       setSelectedOpportunity(null);
//       setOpportunityType('');

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
                  
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select 
//                         options={candidateOptions} 
//                         value={selectedCandidate} 
//                         onChange={handleCandidateSelect} 
//                         placeholder="Search by name or ID..." 
//                         isLoading={isLoading} 
//                         isDisabled={isLoading || isSubmitting} 
//                         isClearable
//                     />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select 
//                             options={opportunityOptions} 
//                             value={selectedOpportunity} 
//                             onChange={handleOpportunitySelect} 
//                             placeholder={isLoadingOpportunities ? "Loading..." : "Select the interview opportunity..."} 
//                             isLoading={isLoadingOpportunities} 
//                             isDisabled={isLoadingOpportunities || isSubmitting} 
//                             isClearable 
//                             noOptionsMessage={() => "No scheduled interviews found for this candidate"}
//                         />
//                       </Form.Group>
                      
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading questions...</p></div>}

//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded shadow-sm border">
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Candidate:</strong> {selectedCandidate?.candidate_name}</p>
//                             <p className="mb-0"><strong>Role:</strong> {selectedOpportunity?.label}</p>
//                         </div>
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
//                             <p className="mb-0"><strong>Interviewer:</strong> {localStorage.getItem("email")}</p>
//                         </div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td>
//                                         <div className="fw-bold">{q.question_text}</div>
//                                       </td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info text-center">
//                             No {evaluationMode === 'general' ? 'general questions' : 'active evaluation parameters'} are configured for this role.
//                         </div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts on the candidate..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check 
//                                         key={opt.value} 
//                                         type="radio" 
//                                         id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name="recommendation"
//                                         checked={recommendations[opt.value]}
//                                         onChange={() => {
//                                             const newState = { ...initialRecommendationState, [opt.value]: true };
//                                             setRecommendations(newState);
//                                         }}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" animation="border" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedCandidate && !isLoading && <div className="text-center p-5 text-muted border rounded bg-light mt-3"><p className="mb-0">Please select a candidate to begin the evaluation.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }


// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// // Initialize recommendation state from options
// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // --- State Management ---
//   const [isLoading, setIsLoading] = useState(false);
  
//   // Data Stores
//   const [masterJobList, setMasterJobList] = useState([]); // All possible job openings from DB
//   const [rawInterviews, setRawInterviews] = useState([]); // Full API response from /interviews/
  
//   // Step 1: Candidate Selection
//   const [candidateOptions, setCandidateOptions] = useState([]);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);

//   // Step 2: Opportunity Selection
//   const [opportunityOptions, setOpportunityOptions] = useState([]);
//   const [selectedOpportunity, setSelectedOpportunity] = useState(null);
//   const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

//   // Step 3: Type (Auto-filled)
//   const [opportunityType, setOpportunityType] = useState('');

//   // Evaluation Form State
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // Dynamic Summary State
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   // --- Helpers ---
//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   const formatOpportunityType = (typeString) => {
//     if (!typeString) return "Opportunity";
//     // Converts "Business_Opportunity" to "Business Opportunity"
//     return typeString.replace(/_/g, ' ');
//   };

//   // --- Effects ---

//   // 1. Initial Data Fetch
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         // 1. Fetch Master Job List (for ID mapping)
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setMasterJobList(jobsResponse.data); // Keep full data for lookups

//         // 2. Fetch All Scheduled Interviews
//         const interviewsResponse = await axios.get(`${API_BASE_URL}/interviews/`);
//         const allInterviews = interviewsResponse.data || [];
//         setRawInterviews(allInterviews);
        
//         // 3. Process Candidates (Unique List)
//         if (allInterviews.length > 0) {
//           const uniqueMap = new Map();
          
//           allInterviews.forEach(interview => {
//              // **FILTER LOGIC:** Only include if 'opening_post' exists and candidate is unique
//              if (interview.opening_post && !uniqueMap.has(interview.candidate_id)) {
//                  uniqueMap.set(interview.candidate_id, {
//                      value: interview.candidate_id, // Value is Candidate ID
//                      label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//                      candidate_name: interview.candidate,
//                      candidate_email: interview.candidate_email
//                  });
//              }
//           });
//           setCandidateOptions(Array.from(uniqueMap.values()));
//         }

//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // 2. Handle Candidate Selection -> Populate Opportunities
//   useEffect(() => {
//     // Reset dependent states
//     setOpportunityOptions([]);
//     setSelectedOpportunity(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedCandidate && rawInterviews.length > 0) {
//         setIsLoadingOpportunities(true);
        
//         // Filter raw interviews for THIS candidate
//         // Also ensure we only show opportunities that HAVE an opening_post (to be consistent with Step 1)
//         const candidateId = selectedCandidate.value;
//         const specificInterviews = rawInterviews.filter(item => 
//             item.candidate_id === candidateId && item.opening_post
//         );
        
//         // Map to dropdown options
//         const opts = specificInterviews.map(interview => {
//             // Determine display label: "Job Title (Type)"
//             const typeLabel = formatOpportunityType(interview.opening_post);
//             const label = `${interview.job_title} (${typeLabel})`;

//             // Try to find matching Job ID from master list (if it's a standard job opening)
//             const masterJob = masterJobList.find(j => j.jobtitle === interview.job_title);

//             return {
//                 value: interview.id, // Crucial: This is the INTERVIEW ID (PK)
//                 label: label,
//                 original_title: interview.job_title,
//                 opening_post: interview.opening_post,
//                 // If found in master list use that ID, otherwise null (for business opps)
//                 master_job_id: masterJob ? masterJob.job_id : null, 
//                 ...interview
//             };
//         });

//         setOpportunityOptions(opts);
//         setIsLoadingOpportunities(false);
//     }
//   }, [selectedCandidate, rawInterviews, masterJobList]);

//   // 3. Fetch Questions when Opportunity Selected
//   useEffect(() => {
//     resetFormState();
//     if (!selectedOpportunity) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       const jobTitle = selectedOpportunity.original_title;
//       const jobId = selectedOpportunity.master_job_id;

//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           // Fetch General Questions based on Job Title
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${jobTitle}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${jobTitle}/interviewer-evaluations/list/`;
          
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);
          
//           if (results[0].status === "fulfilled" && results[0].value.data) rawQuestions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) rawQuestions.push(...results[1].value.data);

//         } else { 
//           // 'parameters' mode
//           if (!jobId) {
//              // If it's a Business Opportunity/Consultant role not in job_opening_list table, 
//              // we might not have parameters linked to an ID. 
//              console.warn("No Master Job ID found for parameters lookup.");
//           } else {
//               const paramsUrl = `${API_BASE_URL}/job-postings/${jobId}/evaluation-parameters/`;
//               const response = await axios.get(paramsUrl);
//               if (response.data?.length > 0) {
//                 rawQuestions = response.data.map(p => ({
//                   id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//                   description: p.description, area: p.area_display,
//                   answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//                   is_active: p.is_active,
//                 }));
//               }
//           }
//         }

//         // Deduplicate
//         const uniqueQuestions = Array.from(new Map(rawQuestions.map(q => [q.id || q.question_text, q])).values());
        
//         // Filter Active
//         const questionsToDisplay = evaluationMode === 'parameters' 
//             ? uniqueQuestions.filter(q => q.is_active === true) 
//             : uniqueQuestions;

//         setEvaluationQuestions(questionsToDisplay);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           console.error("Error fetching questions:", error);
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedOpportunity, evaluationMode]);

//   // Initialize Form Data keys
//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ 
//         ...acc, 
//         [`question_${q.id}_answer`]: "", 
//         [`question_${q.id}_comments`]: "" 
//     }), {}));
//   }, [evaluationQuestions]);


//   // --- Handlers ---

//   const handleCandidateSelect = (option) => {
//     setSelectedCandidate(option);
//   };

//   const handleOpportunitySelect = (option) => {
//     setSelectedOpportunity(option);
//     setEvaluationMode('general'); // Reset mode
    
//     if (option) {
//         setOpportunityType(formatOpportunityType(option.opening_post));
//     } else {
//         setOpportunityType('');
//     }
//   };

//   const handleQuestionChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };

//   // Dynamic List Handlers
//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };
//   const addDynamicListItem = (setter) => setter(prev => [...prev, '']);
//   const removeDynamicListItem = (setter, index) => setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedOpportunity) {
//       Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedCandidate.candidate_email,
//           candidate_name: selectedCandidate.candidate_name,
//           // Use master job ID if available, otherwise sending null/0 usually handled by backend logic for custom opps
//           job_id: selectedOpportunity.master_job_id || null, 
//           job_title: selectedOpportunity.original_title,
//           interviewer_email: localStorage.getItem("email") || "unknown@example.com",
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, 
//             question_text: q.question_text, 
//             area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { 
//         // 'parameters' mode submission
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         // Submit to specific Interview ID
//         const interviewId = selectedOpportunity.value; 
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
        
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
      
//       // Reset selection
//       setSelectedOpportunity(null);
//       setOpportunityType('');
//       // We can keep the candidate selected for convenience, or set null to reset fully
//       // setSelectedCandidate(null); 

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   // Render Helper
//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" 
//             value={formData[`question_${qId}_answer`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
//             required 
//             size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" 
//             rows={2} 
//             value={formData[`question_${qId}_comments`] || ""} 
//             onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
//             placeholder="Comments..." 
//             size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
                  
//                   {/* Step 1: Candidate Selection */}
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
//                     <Select 
//                         options={candidateOptions} 
//                         value={selectedCandidate} 
//                         onChange={handleCandidateSelect} 
//                         placeholder="Search by name or ID..." 
//                         isLoading={isLoading} 
//                         isDisabled={isLoading || isSubmitting} 
//                         isClearable
//                     />
//                   </Form.Group>

//                   {/* Step 2: Opportunity Selection (Only appears after Candidate selected) */}
//                   {selectedCandidate && (
//                     <>
//                       <Form.Group controlId="opportunitySelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
//                         <Select 
//                             options={opportunityOptions} 
//                             value={selectedOpportunity} 
//                             onChange={handleOpportunitySelect} 
//                             placeholder={isLoadingOpportunities ? "Loading..." : "Select the interview opportunity..."} 
//                             isLoading={isLoadingOpportunities} 
//                             isDisabled={isLoadingOpportunities || isSubmitting} 
//                             isClearable 
//                             noOptionsMessage={() => "No scheduled interviews found for this candidate"}
//                         />
//                       </Form.Group>
                      
//                       {/* Step 3: Opportunity Type (Auto-filled) */}
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {/* Mode Selection Buttons */}
//                   {selectedOpportunity && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading questions...</p></div>}

//                   {/* Form Content */}
//                   {!isLoadingQuestions && selectedOpportunity && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded shadow-sm border">
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Candidate:</strong> {selectedCandidate?.candidate_name}</p>
//                             <p className="mb-0"><strong>Role:</strong> {selectedOpportunity?.label}</p>
//                         </div>
//                         <div className="col-md-6">
//                             <p className="mb-1"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
//                             <p className="mb-0"><strong>Interviewer:</strong> {localStorage.getItem("email")}</p>
//                         </div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive className="align-middle">
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "65%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "20%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td>
//                                         <div className="fw-bold">{q.question_text}</div>
//                                       </td>
//                                       {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
//                                       <td>{controls.rating}</td>
//                                       <td>{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info text-center">
//                             No {evaluationMode === 'general' ? 'general questions' : 'active evaluation parameters'} are configured for this role.
//                         </div>
//                       )}
                      
//                       {/* Final Summary Section (Only for Parameters Mode) */}
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4 shadow-sm">
//                             <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={3} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts on the candidate..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div className="d-flex flex-wrap gap-3">
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check 
//                                         key={opt.value} 
//                                         type="radio" 
//                                         id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name="recommendation" // Use same name for radio behavior logic
//                                         checked={recommendations[opt.value]}
//                                         onChange={() => {
//                                             // Reset others, set this one
//                                             const newState = { ...initialRecommendationState, [opt.value]: true };
//                                             setRecommendations(newState);
//                                         }}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-5">
//                         <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" animation="border" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedCandidate && !isLoading && <div className="text-center p-5 text-muted border rounded bg-light mt-3"><p className="mb-0">Please select a candidate to begin the evaluation.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }

// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// // Initialize recommendation state from options
// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // State for the two-step selection flow
//   const [allInterviews, setAllInterviews] = useState([]);
//   const [rawScheduledInterviews, setRawScheduledInterviews] = useState([]); // Store all interviews
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState([]);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
//   // Shared state
//   const [allJobRoles, setAllJobRoles] = useState([]);
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');
//   const [opportunityType, setOpportunityType] = useState('');

//   // MODIFIED STATE: For dynamic summary fields
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     // Reset new dynamic fields
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   // Fetches initial data: scheduled interviews and the master job list
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         // Fetch the master list of all jobs first
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");

//         // Fetch all interviews
//         const interviewsResponse = await axios.get(`${API_BASE_URL}/interviews/`);
//         const allScheduledInterviews = interviewsResponse.data || [];
//         setRawScheduledInterviews(allScheduledInterviews);
        
//         // Filter interviews by the logged-in interviewer for the selection dropdown
//         const userInterviews = allScheduledInterviews.filter(
//             interview => interview.interviewer_email === interviewerEmail
//         );

//         if (userInterviews.length > 0) {
//           const interviewOptions = userInterviews.map(interview => ({
//             value: interview.id,
//             label: `${interview.candidate} (ID: ${interview.candidate_id})`,
//             ...interview,
//           }));
//           setAllInterviews(interviewOptions);
//         } else {
//           setAllInterviews([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // Populates the job dropdown and opportunity type AFTER an interview is selected
//   useEffect(() => {
//     // Reset states when the interview selection changes
//     setAppliedJobs([]);
//     setSelectedJob(null);
//     setOpportunityType('');
//     resetFormState();

//     if (selectedInterview && allJobRoles.length > 0 && rawScheduledInterviews.length > 0) {
//         setIsLoadingJobs(true);

//         // Set opportunity type from the selected interview instance
//         setOpportunityType(selectedInterview.opening_post || '');
        
//         // Find all interviews for the selected candidate to populate all their jobs
//         const candidateId = selectedInterview.candidate_id;
//         const candidateInterviews = rawScheduledInterviews.filter(iv => iv.candidate_id === candidateId);
        
//         // Get unique job titles for that candidate
//         const candidateJobTitles = [...new Set(candidateInterviews.map(iv => iv.job_title))];
        
//         // Match these job titles with the master list of jobs
//         const jobOptions = candidateJobTitles.map(title => 
//           allJobRoles.find(job => job.label === title)
//         ).filter(Boolean); // .filter(Boolean) removes any undefined values if a job isn't found

//         if (jobOptions.length > 0) {
//             setAppliedJobs(jobOptions);
//         } else {
//             Swal.fire("Warning", `No matching jobs found in the master list for this candidate.`, "warning");
//         }

//         setIsLoadingJobs(false);
//     }
//   }, [selectedInterview, allJobRoles, rawScheduledInterviews]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${selectedJob.label}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${selectedJob.label}/interviewer-evaluations/list/`;
          
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);

//           if (results[0].status === "fulfilled" && results[0].value.data) rawQuestions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) rawQuestions.push(...results[1].value.data);

//         } else { // 'parameters' mode
//           const paramsUrl = `${API_BASE_URL}/job-postings/${selectedJob.value}/evaluation-parameters/`;
//           const response = await axios.get(paramsUrl);
//           if (response.data?.length > 0) {
//             rawQuestions = response.data.map(p => ({
//               id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//               description: p.description, area: p.area_display,
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//               is_active: p.is_active,
//             }));
//           }
//         }

//         const uniqueQuestions = Array.from(new Map(rawQuestions.map(q => [q.id || q.question_text, q])).values());

//         let questionsToDisplay;
//         if (evaluationMode === 'parameters') {
//             questionsToDisplay = uniqueQuestions.filter(q => q.is_active === true);
//         } else {
//             questionsToDisplay = uniqueQuestions;
//         }

//         setEvaluationQuestions(questionsToDisplay);
//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", `Failed to load ${evaluationMode} questions.`, "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleInterviewChange = (option) => {
//     setSelectedInterview(option);
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general'); // Reset to general when a new job is chosen
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };

//   const addDynamicListItem = (setter) => {
//     setter(prevList => [...prevList, '']);
//   };

//   const removeDynamicListItem = (setter, index) => {
//     setter(prevList => {
//       if (prevList.length <= 1) return prevList;
//       return prevList.filter((_, i) => i !== index);
//     });
//   };

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedInterview || !selectedJob) {
//       Swal.fire("Warning", "Please select an interview and a job.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedInterview.candidate_email,
//           candidate_name: selectedInterview.candidate,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, question_text: q.question_text, area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedInterview.value; // The interview ID is the value of the selected option
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedInterview(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" value={formData[`question_${qId}_answer`] || ""} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={formData[`question_${qId}_comments`] || ""} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="interviewSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Interview Appointment:</Form.Label>
//                     <Select options={allInterviews} value={selectedInterview} onChange={handleInterviewChange} placeholder="Select an interview..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedInterview && (
//                     <>
//                       <Form.Group controlId="jobTitleSelection" className="mb-4">
//                         <Form.Label className="fw-bold">2. Job for this Interview:</Form.Label>
//                         <Select options={appliedJobs} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobs ? "Loading jobs..." : "Select a job for this candidate..."} isLoading={isLoadingJobs} isDisabled={isLoadingJobs || isSubmitting} isClearable />
//                       </Form.Group>
                      
//                       <Form.Group controlId="opportunityType" className="mb-4">
//                         <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
//                         <Form.Control type="text" value={opportunityType.replace(/_/g, ' ')} readOnly disabled />
//                       </Form.Group>
//                     </>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedInterview?.candidate}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No {evaluationMode === 'general' ? 'general questions' : 'active evaluation parameters'} are configured for this role.</div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div>
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check inline key={opt.value} type="checkbox" id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name={opt.value}
//                                         checked={recommendations[opt.value]}
//                                         onChange={handleRecommendationChange}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedInterview && !isLoading && <div className="text-center p-5 text-muted"><p>Please select an interview to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// }
// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// // Initialize recommendation state from options
// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // State for the two-step selection flow
//   const [allInterviews, setAllInterviews] = useState([]);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState([]);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
//   // Shared state
//   const [allJobRoles, setAllJobRoles] = useState([]);
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // MODIFIED STATE: For dynamic summary fields
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     // Reset new dynamic fields
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   // Fetches initial data: scheduled interviews and the master job list
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");
//         const interviewsResponse = await axios.post(`${API_BASE_URL}/interviewers/`, { email: interviewerEmail });
        
//         if (interviewsResponse.data?.length > 0) {
//           const interviewOptions = interviewsResponse.data.map(interview => ({
//             value: interview.id,
//             label: `${interview.candidate} on ${new Date(interview.date).toLocaleDateString()}`,
//             ...interview,
//           }));
//           setAllInterviews(interviewOptions);
//         } else {
//           setAllInterviews([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // Fetches applied jobs AFTER an interview is selected
//   useEffect(() => {
//     setAppliedJobs([]);
//     setSelectedJob(null);
//     resetFormState();

//     if (selectedInterview?.candidate_email) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobs(true);
//         try {
//           const response = await axios.get(`${API_BASE_URL}/get_applied_job/${candidateEmail}/`);
//           let jobsData = [];
//           if (response.data?.applied_jobs) {
//             jobsData = response.data.applied_jobs.map(job => ({ value: job.job_id, label: job.job_title }));
//           } else if (response.data?.job_titles) {
//             jobsData = allJobRoles.filter(role => response.data.job_titles.includes(role.label));
//           }
//           setAppliedJobs(jobsData);
//         } catch (error) {
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error");
//         } finally {
//           setIsLoadingJobs(false);
//         }
//       };
//       fetchAppliedJobs(selectedInterview.candidate_email);
//     }
//   }, [selectedInterview, allJobRoles]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${selectedJob.label}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${selectedJob.label}/interviewer-evaluations/list/`;
          
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);

//           if (results[0].status === "fulfilled" && results[0].value.data) rawQuestions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) rawQuestions.push(...results[1].value.data);

//         } else { // 'parameters' mode
//           const paramsUrl = `${API_BASE_URL}/job-postings/${selectedJob.value}/evaluation-parameters/`;
//           const response = await axios.get(paramsUrl);
//           if (response.data?.length > 0) {
//             rawQuestions = response.data.map(p => ({
//               id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//               description: p.description, area: p.area_display,
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//               is_active: p.is_active,
//             }));
//           }
//         }

//         // --- MODIFICATION START: Filter logic is re-introduced ---
//         // De-duplicate the combined list of questions
//         const uniqueQuestions = Array.from(new Map(rawQuestions.map(q => [q.id || q.question_text, q])).values());

//         // Conditionally filter questions.
//         // 'General' mode shows everything. 'Parameters' mode now only shows active questions.
//         let questionsToDisplay;
//         if (evaluationMode === 'parameters') {
//             questionsToDisplay = uniqueQuestions.filter(q => q.is_active === true);
//         } else {
//             questionsToDisplay = uniqueQuestions;
//         }

//         setEvaluationQuestions(questionsToDisplay);
//         // --- MODIFICATION END ---

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", `Failed to load ${evaluationMode} questions.`, "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleInterviewChange = (option) => {
//     setSelectedInterview(option);
//     setEvaluationMode('general');
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general');
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };

//   const addDynamicListItem = (setter) => {
//     setter(prevList => [...prevList, '']);
//   };

//   const removeDynamicListItem = (setter, index) => {
//     setter(prevList => {
//       if (prevList.length <= 1) return prevList;
//       return prevList.filter((_, i) => i !== index);
//     });
//   };

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedInterview || !selectedJob) {
//       Swal.fire("Warning", "Please select an interview and a job.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedInterview.candidate_email,
//           candidate_name: selectedInterview.candidate,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, question_text: q.question_text, area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedInterview.id;
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: selectedRecs[0], 
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedInterview(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" value={formData[`question_${qId}_answer`] || ""} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={formData[`question_${qId}_comments`] || ""} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="interviewSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Interview Appointment:</Form.Label>
//                     <Select options={allInterviews} value={selectedInterview} onChange={handleInterviewChange} placeholder="Select an interview..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedInterview && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">2. Select Job for this Interview:</Form.Label>
//                       <Select options={appliedJobs} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobs ? "Loading jobs..." : "Select job title..."} isLoading={isLoadingJobs} isDisabled={isLoadingJobs || isSubmitting || !appliedJobs.length} isClearable/>
//                     </Form.Group>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedInterview?.candidate}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary text-capitalize">{area.replace(/_/g, ' ')}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No {evaluationMode === 'general' ? 'general questions' : 'active evaluation parameters'} are configured for this role.</div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div>
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check inline key={opt.value} type="checkbox" id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name={opt.value}
//                                         checked={recommendations[opt.value]}
//                                         onChange={handleRecommendationChange}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedInterview && !isLoading && <div className="text-center p-5 text-muted"><p>Please select an interview to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// }

// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// // Initialize recommendation state from options
// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // State for the two-step selection flow
//   const [allInterviews, setAllInterviews] = useState([]);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState([]);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
//   // Shared state
//   const [allJobRoles, setAllJobRoles] = useState([]);
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // MODIFIED STATE: For dynamic summary fields
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     // Reset new dynamic fields
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   // Fetches initial data: scheduled interviews and the master job list
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");
//         const interviewsResponse = await axios.post(`${API_BASE_URL}/interviewers/`, { email: interviewerEmail });
        
//         if (interviewsResponse.data?.length > 0) {
//           const interviewOptions = interviewsResponse.data.map(interview => ({
//             value: interview.id,
//             label: `${interview.candidate} on ${new Date(interview.date).toLocaleDateString()}`,
//             ...interview,
//           }));
//           setAllInterviews(interviewOptions);
//         } else {
//           setAllInterviews([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // Fetches applied jobs AFTER an interview is selected
//   useEffect(() => {
//     setAppliedJobs([]);
//     setSelectedJob(null);
//     resetFormState();

//     if (selectedInterview?.candidate_email) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobs(true);
//         try {
//           const response = await axios.get(`${API_BASE_URL}/get_applied_job/${candidateEmail}/`);
//           let jobsData = [];
//           if (response.data?.applied_jobs) {
//             jobsData = response.data.applied_jobs.map(job => ({ value: job.job_id, label: job.job_title }));
//           } else if (response.data?.job_titles) {
//             jobsData = allJobRoles.filter(role => response.data.job_titles.includes(role.label));
//           }
//           setAppliedJobs(jobsData);
//         } catch (error) {
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error");
//         } finally {
//           setIsLoadingJobs(false);
//         }
//       };
//       fetchAppliedJobs(selectedInterview.candidate_email);
//     }
//   }, [selectedInterview, allJobRoles]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       let rawQuestions = [];
//       try {
//         if (evaluationMode === 'general') {
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${selectedJob.label}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${selectedJob.label}/interviewer-evaluations/list/`;
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);
//           if (results[0].status === "fulfilled" && results[0].value.data) rawQuestions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) rawQuestions.push(...results[1].value.data);
//         } else { // 'parameters' mode
//           const paramsUrl = `${API_BASE_URL}/job-postings/${selectedJob.value}/evaluation-parameters/`;
//           const response = await axios.get(paramsUrl);
//           if (response.data?.length > 0) {
//             rawQuestions = response.data.map(p => ({
//               id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//               description: p.description, area: p.area_display,
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//               is_active: p.is_active, // Pass the is_active status from the API response
//             }));
//           }
//         }

//         // De-duplicate the combined list of questions
//         const uniqueQuestions = Array.from(new Map(rawQuestions.map(q => [q.id || q.question_text, q])).values());

//         // **MODIFIED**: Filter questions conditionally.
//         // Only filter by is_active for 'parameters' mode. Show all for 'general' mode.
//         let questionsToDisplay;
//         if (evaluationMode === 'parameters') {
//             questionsToDisplay = uniqueQuestions.filter(q => q.is_active === true);
//         } else {
//             questionsToDisplay = uniqueQuestions;
//         }

//         setEvaluationQuestions(questionsToDisplay);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", `Failed to load ${evaluationMode} questions.`, "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleInterviewChange = (option) => {
//     setSelectedInterview(option);
//     setEvaluationMode('general');
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general');
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   // --- NEW HANDLERS FOR DYNAMIC LISTS AND CHECKBOXES ---
//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };

//   const addDynamicListItem = (setter) => {
//     setter(prevList => [...prevList, '']);
//   };

//   const removeDynamicListItem = (setter, index) => {
//     setter(prevList => {
//       if (prevList.length <= 1) return prevList; // Don't remove the last item
//       return prevList.filter((_, i) => i !== index);
//     });
//   };

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };
//   // --- END OF NEW HANDLERS ---

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedInterview || !selectedJob) {
//       Swal.fire("Warning", "Please select an interview and a job.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedInterview.candidate_email,
//           candidate_name: selectedInterview.candidate,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, question_text: q.question_text, area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedInterview.id;
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           // Send first selected recommendation to match singular backend field
//           overall_recommendation: selectedRecs[0], 
//           // Filter out empty strings from lists
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedInterview(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" value={formData[`question_${qId}_answer`] || ""} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={formData[`question_${qId}_comments`] || ""} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="interviewSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Interview Appointment:</Form.Label>
//                     <Select options={allInterviews} value={selectedInterview} onChange={handleInterviewChange} placeholder="Select an interview..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedInterview && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">2. Select Job for this Interview:</Form.Label>
//                       <Select options={appliedJobs} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobs ? "Loading jobs..." : "Select job title..."} isLoading={isLoadingJobs} isDisabled={isLoadingJobs || isSubmitting || !appliedJobs.length} isClearable/>
//                     </Form.Group>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedInterview?.candidate}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary">{area}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No {evaluationMode === 'general' ? 'general questions' : 'active parameters'} are configured for this role.</div>
//                       )}
                      
//                       {/* === MODIFIED Final Summary section === */}
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div>
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check inline key={opt.value} type="checkbox" id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name={opt.value}
//                                         checked={recommendations[opt.value]}
//                                         onChange={handleRecommendationChange}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedInterview && !isLoading && <div className="text-center p-5 text-muted"><p>Please select an interview to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// }


// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// // Initialize recommendation state from options
// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // State for the two-step selection flow
//   const [allInterviews, setAllInterviews] = useState([]);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState([]);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
//   // Shared state
//   const [allJobRoles, setAllJobRoles] = useState([]);
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // MODIFIED STATE: For dynamic summary fields
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     // Reset new dynamic fields
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   // Fetches initial data: scheduled interviews and the master job list
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");
//         const interviewsResponse = await axios.post(`${API_BASE_URL}/interviewers/`, { email: interviewerEmail });
        
//         if (interviewsResponse.data?.length > 0) {
//           const interviewOptions = interviewsResponse.data.map(interview => ({
//             value: interview.id,
//             label: `${interview.candidate} on ${new Date(interview.date).toLocaleDateString()}`,
//             ...interview,
//           }));
//           setAllInterviews(interviewOptions);
//         } else {
//           setAllInterviews([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // Fetches applied jobs AFTER an interview is selected
//   useEffect(() => {
//     setAppliedJobs([]);
//     setSelectedJob(null);
//     resetFormState();

//     if (selectedInterview?.candidate_email) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobs(true);
//         try {
//           const response = await axios.get(`${API_BASE_URL}/get_applied_job/${candidateEmail}/`);
//           let jobsData = [];
//           if (response.data?.applied_jobs) {
//             jobsData = response.data.applied_jobs.map(job => ({ value: job.job_id, label: job.job_title }));
//           } else if (response.data?.job_titles) {
//             jobsData = allJobRoles.filter(role => response.data.job_titles.includes(role.label));
//           }
//           setAppliedJobs(jobsData);
//         } catch (error) {
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error");
//         } finally {
//           setIsLoadingJobs(false);
//         }
//       };
//       fetchAppliedJobs(selectedInterview.candidate_email);
//     }
//   }, [selectedInterview, allJobRoles]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       let questions = [];
//       try {
//         if (evaluationMode === 'general') {
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${selectedJob.label}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${selectedJob.label}/interviewer-evaluations/list/`;
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);
//           if (results[0].status === "fulfilled" && results[0].value.data) questions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) questions.push(...results[1].value.data);
//         } else { // 'parameters' mode
//           const paramsUrl = `${API_BASE_URL}/job-postings/${selectedJob.value}/evaluation-parameters/`;
//           const response = await axios.get(paramsUrl);
//           if (response.data?.length > 0) {
//             questions = response.data.map(p => ({
//               id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//               description: p.description, area: p.area_display,
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//               is_active: p.is_active, // Pass the is_active status from the API response
//             }));
//           }
//         }

//         // De-duplicate the combined list of questions
//         const uniqueQuestions = Array.from(new Map(questions.map(q => [q.id || q.question_text, q])).values());

//         // **MODIFIED**: Filter questions to only include those with `is_active: true`
//         const activeQuestions = uniqueQuestions.filter(q => q.is_active === true);

//         setEvaluationQuestions(activeQuestions);

//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", `Failed to load ${evaluationMode} questions.`, "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleInterviewChange = (option) => {
//     setSelectedInterview(option);
//     setEvaluationMode('general');
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general');
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   // --- NEW HANDLERS FOR DYNAMIC LISTS AND CHECKBOXES ---
//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };

//   const addDynamicListItem = (setter) => {
//     setter(prevList => [...prevList, '']);
//   };

//   const removeDynamicListItem = (setter, index) => {
//     setter(prevList => {
//       if (prevList.length <= 1) return prevList; // Don't remove the last item
//       return prevList.filter((_, i) => i !== index);
//     });
//   };

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };
//   // --- END OF NEW HANDLERS ---

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedInterview || !selectedJob) {
//       Swal.fire("Warning", "Please select an interview and a job.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedInterview.candidate_email,
//           candidate_name: selectedInterview.candidate,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, question_text: q.question_text, area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedInterview.id;
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           // Send first selected recommendation to match singular backend field
//           overall_recommendation: selectedRecs[0], 
//           // Filter out empty strings from lists
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedInterview(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" value={formData[`question_${qId}_answer`] || ""} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={formData[`question_${qId}_comments`] || ""} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="interviewSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Interview Appointment:</Form.Label>
//                     <Select options={allInterviews} value={selectedInterview} onChange={handleInterviewChange} placeholder="Select an interview..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedInterview && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">2. Select Job for this Interview:</Form.Label>
//                       <Select options={appliedJobs} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobs ? "Loading jobs..." : "Select job title..."} isLoading={isLoadingJobs} isDisabled={isLoadingJobs || isSubmitting || !appliedJobs.length} isClearable/>
//                     </Form.Group>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedInterview?.candidate}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary">{area}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No active {evaluationMode === 'general' ? 'general questions' : 'parameters'} are configured for this role.</div>
//                       )}
                      
//                       {/* === MODIFIED Final Summary section === */}
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div>
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check inline key={opt.value} type="checkbox" id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name={opt.value}
//                                         checked={recommendations[opt.value]}
//                                         onChange={handleRecommendationChange}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedInterview && !isLoading && <div className="text-center p-5 text-muted"><p>Please select an interview to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// } 

// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"


// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// // Initialize recommendation state from options
// const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
//     acc[opt.value] = false;
//     return acc;
// }, {});

// export default function EvaluationForm() {
//   // State for the two-step selection flow
//   const [allInterviews, setAllInterviews] = useState([]);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState([]);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
//   // Shared state
//   const [allJobRoles, setAllJobRoles] = useState([]);
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // MODIFIED STATE: For dynamic summary fields
//   const [strengths, setStrengths] = useState(['']);
//   const [developmentAreas, setDevelopmentAreas] = useState(['']);
//   const [finalComments, setFinalComments] = useState("");
//   const [recommendations, setRecommendations] = useState(initialRecommendationState);


//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     // Reset new dynamic fields
//     setStrengths(['']);
//     setDevelopmentAreas(['']);
//     setFinalComments('');
//     setRecommendations(initialRecommendationState);
//   }

//   // Fetches initial data: scheduled interviews and the master job list
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");
//         const interviewsResponse = await axios.post(`${API_BASE_URL}/interviewers/`, { email: interviewerEmail });
        
//         if (interviewsResponse.data?.length > 0) {
//           const interviewOptions = interviewsResponse.data.map(interview => ({
//             value: interview.id,
//             label: `${interview.candidate} on ${new Date(interview.date).toLocaleDateString()}`,
//             ...interview,
//           }));
//           setAllInterviews(interviewOptions);
//         } else {
//           setAllInterviews([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // Fetches applied jobs AFTER an interview is selected
//   useEffect(() => {
//     setAppliedJobs([]);
//     setSelectedJob(null);
//     resetFormState();

//     if (selectedInterview?.candidate_email) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobs(true);
//         try {
//           const response = await axios.get(`${API_BASE_URL}/get_applied_job/${candidateEmail}/`);
//           let jobsData = [];
//           if (response.data?.applied_jobs) {
//             jobsData = response.data.applied_jobs.map(job => ({ value: job.job_id, label: job.job_title }));
//           } else if (response.data?.job_titles) {
//             jobsData = allJobRoles.filter(role => response.data.job_titles.includes(role.label));
//           }
//           setAppliedJobs(jobsData);
//         } catch (error) {
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error");
//         } finally {
//           setIsLoadingJobs(false);
//         }
//       };
//       fetchAppliedJobs(selectedInterview.candidate_email);
//     }
//   }, [selectedInterview, allJobRoles]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       let questions = [];
//       try {
//         if (evaluationMode === 'general') {
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${selectedJob.label}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${selectedJob.label}/interviewer-evaluations/list/`;
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);
//           if (results[0].status === "fulfilled" && results[0].value.data) questions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) questions.push(...results[1].value.data);
//         } else { // 'parameters' mode
//           const paramsUrl = `${API_BASE_URL}/job-postings/${selectedJob.value}/evaluation-parameters/`;
//           const response = await axios.get(paramsUrl);
//           if (response.data?.length > 0) {
//             questions = response.data.map(p => ({
//               id: `param_${p.id}`, original_id: p.id, question_text: p.parameter_text,
//               description: p.description, area: p.area_display,
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//             }));
//           }
//         }
//         setEvaluationQuestions(Array.from(new Map(questions.map(q => [q.id || q.question_text, q])).values()));
//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", `Failed to load ${evaluationMode} questions.`, "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleInterviewChange = (option) => {
//     setSelectedInterview(option);
//     setEvaluationMode('general');
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general');
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   // --- NEW HANDLERS FOR DYNAMIC LISTS AND CHECKBOXES ---
//   const handleDynamicListChange = (setter, index, value) => {
//     setter(prevList => {
//       const newList = [...prevList];
//       newList[index] = value;
//       return newList;
//     });
//   };

//   const addDynamicListItem = (setter) => {
//     setter(prevList => [...prevList, '']);
//   };

//   const removeDynamicListItem = (setter, index) => {
//     setter(prevList => {
//       if (prevList.length <= 1) return prevList; // Don't remove the last item
//       return prevList.filter((_, i) => i !== index);
//     });
//   };

//   const handleRecommendationChange = (e) => {
//     const { name, checked } = e.target;
//     setRecommendations(prev => ({...prev, [name]: checked}));
//   };
//   // --- END OF NEW HANDLERS ---

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedInterview || !selectedJob) {
//       Swal.fire("Warning", "Please select an interview and a job.", "warning");
//       return;
//     }
//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedInterview.candidate_email,
//           candidate_name: selectedInterview.candidate,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, question_text: q.question_text, area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
//         if (selectedRecs.length === 0) {
//             Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         const interviewId = selectedInterview.id;
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           // Send first selected recommendation to match singular backend field
//           overall_recommendation: selectedRecs[0], 
//           // Filter out empty strings from lists
//           strengths: strengths.map(s => s.trim()).filter(s => s),
//           development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedInterview(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     return {
//       rating: (
//         <Form.Control as="select" value={formData[`question_${qId}_answer`] || ""} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={formData[`question_${qId}_comments`] || ""} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="interviewSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Interview Appointment:</Form.Label>
//                     <Select options={allInterviews} value={selectedInterview} onChange={handleInterviewChange} placeholder="Select an interview..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedInterview && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">2. Select Job for this Interview:</Form.Label>
//                       <Select options={appliedJobs} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobs ? "Loading jobs..." : "Select job title..."} isLoading={isLoadingJobs} isDisabled={isLoadingJobs || isSubmitting || !appliedJobs.length} isClearable/>
//                     </Form.Group>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedInterview?.candidate}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary">{area}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No {evaluationMode === 'general' ? 'general questions' : 'parameters'} are configured for this role.</div>
//                       )}
                      
//                       {/* === MODIFIED Final Summary section === */}
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     {strengths.map((strength, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} placeholder={`Strength #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength
//                                     </Button>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                      {developmentAreas.map((area, index) => (
//                                         <InputGroup key={index} className="mb-2">
//                                             <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} placeholder={`Area #${index + 1}`}/>
//                                             <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}>
//                                                 <FontAwesomeIcon icon={faTrash} />
//                                             </Button>
//                                         </InputGroup>
//                                     ))}
//                                     <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}>
//                                         <FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area
//                                     </Button>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <div>
//                                 {RECOMMENDATION_OPTIONS.map(opt => (
//                                     <Form.Check inline key={opt.value} type="checkbox" id={`rec-${opt.value}`}
//                                         label={opt.label}
//                                         name={opt.value}
//                                         checked={recommendations[opt.value]}
//                                         onChange={handleRecommendationChange}
//                                     />
//                                 ))}
//                                 </div>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedInterview && !isLoading && <div className="text-center p-5 text-muted"><p>Please select an interview to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// } 


// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// export default function EvaluationForm() {
//   // State for the new two-step selection flow
//   const [allInterviews, setAllInterviews] = useState([]);
//   const [selectedInterview, setSelectedInterview] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState([]);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
//   // Shared state
//   const [allJobRoles, setAllJobRoles] = useState([]);
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // State for the parameter summary fields
//   const [recommendation, setRecommendation] = useState("");
//   const [strengths, setStrengths] = useState("");
//   const [developmentAreas, setDevelopmentAreas] = useState("");
//   const [finalComments, setFinalComments] = useState("");

//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setRecommendation("");
//     setStrengths("");
//     setDevelopmentAreas("");
//     setFinalComments("");
//   }

//   // Fetches all necessary initial data: scheduled interviews and the master job list
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoading(true);
//       try {
//         // Fetch master list of all jobs to resolve titles to IDs later
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         // Fetch scheduled interviews for this interviewer
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");
//         const interviewsResponse = await axios.post(`${API_BASE_URL}/interviewers/`, { email: interviewerEmail });
        
//         if (interviewsResponse.data?.length > 0) {
//           const interviewOptions = interviewsResponse.data.map(interview => ({
//             value: interview.id, // The unique interview ID
//             label: `${interview.candidate} on ${new Date(interview.date).toLocaleDateString()}`,
//             // Store the full object for easy access
//             ...interview,
//           }));
//           setAllInterviews(interviewOptions);
//         } else {
//           setAllInterviews([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoading(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // **New Chained Logic**: Fetches applied jobs AFTER an interview is selected
//   useEffect(() => {
//     // Clear jobs and questions whenever the selected interview changes
//     setAppliedJobs([]);
//     setSelectedJob(null);
//     resetFormState();

//     if (selectedInterview?.candidate_email) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobs(true);
//         try {
//           const response = await axios.get(`${API_BASE_URL}/get_applied_job/${candidateEmail}/`);
//           let jobsData = [];
//           if (response.data?.applied_jobs) {
//             jobsData = response.data.applied_jobs.map(job => ({ value: job.job_id, label: job.job_title }));
//           } else if (response.data?.job_titles) {
//             jobsData = allJobRoles.filter(role => response.data.job_titles.includes(role.label));
//           }
//           setAppliedJobs(jobsData);
//         } catch (error) {
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error");
//         } finally {
//           setIsLoadingJobs(false);
//         }
//       };
//       fetchAppliedJobs(selectedInterview.candidate_email);
//     }
//   }, [selectedInterview, allJobRoles]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchQuestions = async () => {
//       setIsLoadingQuestions(true);
//       let questions = [];
//       try {
//         if (evaluationMode === 'general') {
//           const simpleUrl = `${API_BASE_URL}/evaluation_questions/${selectedJob.label}/`;
//           const areaUrl = `${API_BASE_URL}/jobs/title/${selectedJob.label}/interviewer-evaluations/list/`;
//           const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);
//           if (results[0].status === "fulfilled" && results[0].value.data) questions.push(...results[0].value.data);
//           if (results[1].status === "fulfilled" && results[1].value.data) questions.push(...results[1].value.data);
//         } else { // 'parameters' mode
//           const paramsUrl = `${API_BASE_URL}/job-postings/${selectedJob.value}/evaluation-parameters/`;
//           const response = await axios.get(paramsUrl);
//           if (response.data?.length > 0) {
//             questions = response.data.map(p => ({
//               id: `param_${p.id}`,
//               original_id: p.id,
//               question_text: p.parameter_text,
//               description: p.description,
//               area: p.area_display,
//               answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//             }));
//           }
//         }
//         setEvaluationQuestions(Array.from(new Map(questions.map(q => [q.id || q.question_text, q])).values()));
//       } catch (error) {
//         if (error.response?.status !== 404) {
//           Swal.fire("Error", `Failed to load ${evaluationMode} questions.`, "error");
//         }
//       } finally {
//         setIsLoadingQuestions(false);
//       }
//     };
//     fetchQuestions();
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleInterviewChange = (option) => {
//     setSelectedInterview(option);
//     setEvaluationMode('general');
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general');
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedInterview || !selectedJob) {
//       Swal.fire("Warning", "Please select an interview and a job.", "warning");
//       return;
//     }

//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedInterview.candidate_email,
//           candidate_name: selectedInterview.candidate,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id, question_text: q.question_text, area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         if (!recommendation) {
//             Swal.fire("Validation Error", "Please select an overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }
        
//         // Use the ID from the selected interview object
//         const interviewId = selectedInterview.id;
//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: recommendation,
//           strengths: strengths,
//           development_areas: developmentAreas,
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedInterview(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     const answerValue = formData[`question_${qId}_answer`] || "";
//     const commentsValue = formData[`question_${qId}_comments`] || "";
//     return {
//       rating: (
//         <Form.Control as="select" value={answerValue} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={commentsValue} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="interviewSelection" className="mb-4">
//                     <Form.Label className="fw-bold">1. Select Interview Appointment:</Form.Label>
//                     <Select options={allInterviews} value={selectedInterview} onChange={handleInterviewChange} placeholder="Select an interview..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedInterview && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">2. Select Job for this Interview:</Form.Label>
//                       <Select options={appliedJobs} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobs ? "Loading jobs..." : "Select job title..."} isLoading={isLoadingJobs} isDisabled={isLoadingJobs || isSubmitting || !appliedJobs.length} isClearable/>
//                     </Form.Group>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedInterview?.candidate}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary">{area}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No {evaluationMode === 'general' ? 'general questions' : 'parameters'} are configured for this role.</div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     <Form.Control as="textarea" rows={3} value={strengths} onChange={e => setStrengths(e.target.value)} placeholder="Key strengths observed..."/>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                     <Form.Control as="textarea" rows={3} value={developmentAreas} onChange={e => setDevelopmentAreas(e.target.value)} placeholder="Areas the candidate can improve..."/>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <Form.Select value={recommendation} onChange={e => setRecommendation(e.target.value)} required>
//                                     <option value="">-- Select Recommendation --</option>
//                                     {RECOMMENDATION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//                                 </Form.Select>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedInterview && !isLoading && <div className="text-center p-5 text-muted"><p>Please select an interview to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// }



// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table, ButtonGroup, Row, Col } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional", numeric: 5 },
//   { value: "Above Average", label: "Above Average", numeric: 4 },
//   { value: "Average", label: "Average", numeric: 3 },
//   { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
//   { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
// ]

// const RECOMMENDATION_OPTIONS = [
//   { value: "STRONG_RECOMMEND", label: "Strongly Recommend" },
//   { value: "RECOMMEND", label: "Recommend" },
//   { value: "NEUTRAL", label: "Neutral" },
//   { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
// ]

// export default function EvaluationForm() {
//   // Core state for the two-dropdown system
//   const [allCandidates, setAllCandidates] = useState([])
//   const [selectedCandidate, setSelectedCandidate] = useState(null)
//   const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)
//   const [appliedJobTitles, setAppliedJobTitles] = useState([])
//   const [selectedJob, setSelectedJob] = useState(null)
//   const [isLoadingJobTitles, setIsLoadingJobTitles] = useState(false)
//   const [allJobRoles, setAllJobRoles] = useState([]);
  
//   // NEW STATE: To store the evaluation records which contain the necessary ID
//   const [allEvaluations, setAllEvaluations] = useState([]);

//   // Shared state
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [evaluationMode, setEvaluationMode] = useState('general');

//   // State for the parameter summary fields
//   const [recommendation, setRecommendation] = useState("");
//   const [strengths, setStrengths] = useState("");
//   const [developmentAreas, setDevelopmentAreas] = useState("");
//   const [finalComments, setFinalComments] = useState("");

//   const resetFormState = () => {
//     setEvaluationQuestions([]);
//     setFormData({});
//     setRecommendation("");
//     setStrengths("");
//     setDevelopmentAreas("");
//     setFinalComments("");
//   }

//   // Fetches all necessary initial data
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       setIsLoadingCandidates(true);
//       try {
//         // Fetch the list of all evaluations to find the ID later
//         const evaluationsResponse = await axios.get(`${API_BASE_URL}/interview-evaluations/`);
//         if (evaluationsResponse.data?.results) {
//           setAllEvaluations(evaluationsResponse.data.results);
//         }

//         // Fetch master list of all jobs
//         const jobsResponse = await axios.get(`${API_BASE_URL}/job_opening_list/`);
//         setAllJobRoles(jobsResponse.data.map(job => ({ value: job.job_id, label: job.jobtitle })));
        
//         // Fetch candidates assigned to this interviewer
//         const interviewerEmail = localStorage.getItem("email");
//         if (!interviewerEmail) throw new Error("Interviewer email not found.");
//         const candidatesResponse = await axios.post(`${API_BASE_URL}/interviewers/`, { email: interviewerEmail });
        
//         if (candidatesResponse.data?.length > 0) {
//           setAllCandidates(candidatesResponse.data.map(cand => ({
//             value: cand.candidate_email,
//             label: `${cand.candidate || cand.candidate_name} (Email: ${cand.candidate_email})`,
//             candidateName: cand.candidate || cand.candidate_name,
//           })));
//         } else {
//           setAllCandidates([]);
//         }
//       } catch (error) {
//         Swal.fire("Error", `Failed to fetch initial data: ${error.message}`, "error");
//       } finally {
//         setIsLoadingCandidates(false);
//       }
//     };
//     fetchInitialData();
//   }, []);

//   // Fetches applied jobs for the selected candidate
//   useEffect(() => {
//     if (selectedCandidate?.value) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobTitles(true);
//         setAppliedJobTitles([]);
//         setSelectedJob(null);
//         try {
//           const response = await axios.get(`${API_BASE_URL}/get_applied_job/${candidateEmail}/`);
//           let jobsData = [];
//           if (response.data?.applied_jobs) {
//             jobsData = response.data.applied_jobs.map(job => ({ value: job.job_id, label: job.job_title }));
//           } else if (response.data?.job_titles) {
//             jobsData = allJobRoles.filter(role => response.data.job_titles.includes(role.label));
//           }
//           setAppliedJobTitles(jobsData);
//         } catch (error) {
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error");
//         } finally {
//           setIsLoadingJobTitles(false);
//         }
//       };
//       fetchAppliedJobs(selectedCandidate.value);
//     } else {
//       setAppliedJobTitles([]);
//       setSelectedJob(null);
//     }
//   }, [selectedCandidate, allJobRoles]);

//   // Fetches questions based on selected job and evaluation mode
//   useEffect(() => {
//     resetFormState();
//     if (!selectedJob?.value) return;

//     const fetchGeneralQuestions = async (jobTitle) => {
//         setIsLoadingQuestions(true);
//         const simpleUrl = `${API_BASE_URL}/evaluation_questions/${jobTitle}/`;
//         const areaUrl = `${API_BASE_URL}/jobs/title/${jobTitle}/interviewer-evaluations/list/`;
//         try {
//             const results = await Promise.allSettled([axios.get(simpleUrl), axios.get(areaUrl)]);
//             const combined = [];
//             if (results[0].status === "fulfilled" && results[0].value.data) combined.push(...results[0].value.data);
//             if (results[1].status === "fulfilled" && results[1].value.data) combined.push(...results[1].value.data);
//             setEvaluationQuestions(Array.from(new Map(combined.map(q => [q.id || q.question_text, q])).values()));
//         } catch (error) {
//             Swal.fire("Error", "Failed to load general evaluation questions.", "error");
//         } finally {
//             setIsLoadingQuestions(false);
//         }
//     };

//     const fetchParameters = async (jobId) => {
//         setIsLoadingQuestions(true);
//         const url = `${API_BASE_URL}/job-postings/${jobId}/evaluation-parameters/`;
//         try {
//             const response = await axios.get(url);
//             if (response.data?.length > 0) {
//                 setEvaluationQuestions(response.data.map(p => ({
//                     id: `param_${p.id}`,
//                     original_id: p.id,
//                     question_text: p.parameter_text,
//                     description: p.description,
//                     area: p.area_display,
//                     answers: RATING_SCALE_OPTIONS.map(opt => opt.value),
//                 })));
//             }
//         } catch (error) {
//             if (error.response?.status !== 404) {
//                 Swal.fire("Error", "Failed to load evaluation parameters.", "error");
//             }
//         } finally {
//             setIsLoadingQuestions(false);
//         }
//     };

//     if (evaluationMode === 'general') {
//         fetchGeneralQuestions(selectedJob.label);
//     } else {
//         fetchParameters(selectedJob.value);
//     }
//   }, [selectedJob, evaluationMode]);

//   useEffect(() => {
//     setFormData(evaluationQuestions.reduce((acc, q) => ({ ...acc, [`question_${q.id}_answer`]: "", [`question_${q.id}_comments`]: "" }), {}));
//   }, [evaluationQuestions]);

//   const handleCandidateChange = (option) => {
//     setSelectedCandidate(option);
//     setEvaluationMode('general');
//   };

//   const handleJobChange = (option) => {
//     setSelectedJob(option);
//     setEvaluationMode('general');
//   };

//   const handleChange = (id, fieldType, value) => {
//     setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
//   };
  
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedCandidate || !selectedJob) {
//       Swal.fire("Warning", "Please select a candidate and job.", "warning");
//       return;
//     }

//     setIsSubmitting(true);
    
//     try {
//       let submissionUrl = "";
//       let payload = {};

//       if (evaluationMode === 'general') {
//         submissionUrl = `${API_BASE_URL}/save_evaluation/`;
//         payload = {
//           candidate_email: selectedCandidate.value,
//           candidate_name: selectedCandidate.candidateName,
//           job_id: selectedJob.value,
//           job_title: selectedJob.label,
//           interviewer_email: localStorage.getItem("email"),
//           evaluation_details: evaluationQuestions.map(q => ({
//             question_id: q.id,
//             question_text: q.question_text,
//             area: q.area || "General",
//             answer_value: formData[`question_${q.id}_answer`] || "",
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       } else { // 'parameters' mode
//         if (!recommendation) {
//             Swal.fire("Validation Error", "Please select an overall recommendation.", "warning");
//             setIsSubmitting(false);
//             return;
//         }

//         // --- Find the Interview ID from the fetched evaluations ---
//         const relevantEvaluation = allEvaluations.find(
//           evalRecord => evalRecord.candidate_email === selectedCandidate.value && evalRecord.job_id === selectedJob.value
//         );

//         if (!relevantEvaluation) {
//           throw new Error("A matching interview record could not be found to submit this evaluation. Please ensure an initial evaluation exists.");
//         }
        
//         const interviewId = relevantEvaluation.id;
//         // --- End of find logic ---

//         submissionUrl = `${API_BASE_URL}/interviews/${interviewId}/submit-evaluation/`;
//         const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
        
//         payload = {
//           overall_recommendation: recommendation,
//           strengths: strengths,
//           development_areas: developmentAreas,
//           final_comments: finalComments,
//           responses: evaluationQuestions.map(q => ({
//             parameter: q.original_id,
//             rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
//             comments: formData[`question_${q.id}_comments`] || "",
//           })),
//         };
//       }

//       await axios.post(submissionUrl, payload);
//       Swal.fire("Success", "Evaluation submitted successfully!", "success");
//       setSelectedCandidate(null); // Full reset

//     } catch (error) {
//       const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
//       Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const qId = question.id;
//     const answerValue = formData[`question_${qId}_answer`] || "";
//     const commentsValue = formData[`question_${qId}_comments`] || "";
//     // Simplified logic as both modes now use the rating scale for this part
//     return {
//       rating: (
//         <Form.Control as="select" value={answerValue} onChange={(e) => handleChange(qId, "answer", e.target.value)} required size="sm">
//           <option value="">-- Select Rating --</option>
//           {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//         </Form.Control>
//       ),
//       comments: (
//         <Form.Control as="textarea" rows={2} value={commentsValue} onChange={(e) => handleChange(qId, "comments", e.target.value)} placeholder="Provide evidence..." size="sm"/>
//       ),
//     };
//   };

//   const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
//     const area = q.area || "General Questions";
//     if (!groups[area]) groups[area] = [];
//     groups[area].push(q);
//     return groups;
//   }, {});

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white"><h2 className="mb-0 h4">Candidate Interview Evaluation</h2></div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">Select Candidate:</Form.Label>
//                     <Select options={allCandidates} value={selectedCandidate} onChange={handleCandidateChange} placeholder="Select a candidate..." isLoading={isLoadingCandidates} isDisabled={isLoadingCandidates || isSubmitting} isClearable/>
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">Select Job Applied For:</Form.Label>
//                       <Select options={appliedJobTitles} value={selectedJob} onChange={handleJobChange} placeholder={isLoadingJobTitles ? "Loading..." : "Select job title..."} isLoading={isLoadingJobTitles} isDisabled={isLoadingJobTitles || isSubmitting || !appliedJobTitles.length} isClearable/>
//                     </Form.Group>
//                   )}
                  
//                   {selectedJob && (
//                     <div className="text-center mb-4">
//                       <ButtonGroup>
//                         <Button variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('general')} disabled={isLoadingQuestions}>General Questions</Button>
//                         <Button variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} onClick={() => setEvaluationMode('parameters')} disabled={isLoadingQuestions}>Other Parameters</Button>
//                       </ButtonGroup>
//                     </div>
//                   )}

//                   {isLoadingQuestions && <div className="text-center my-4"><Spinner /><p className="mt-2">Loading data...</p></div>}

//                   {!isLoadingQuestions && selectedJob && (
//                     <>
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6"><p><strong>Candidate:</strong> {selectedCandidate?.candidateName}</p><p><strong>Position:</strong> {selectedJob?.label}</p></div>
//                         <div className="col-md-6"><p><strong>Date:</strong> {new Date().toLocaleDateString()}</p><p><strong>Interviewer:</strong> {localStorage.getItem("email")}</p></div>
//                       </div>

//                       {evaluationQuestions.length > 0 ? (
//                         Object.entries(groupedQuestions).map(([area, questions]) => (
//                           <div key={area} className="mb-5">
//                             <h4 className="mb-3 text-primary">{area}</h4>
//                             <Table striped bordered hover responsive>
//                               <thead className="table-dark">
//                                 <tr>
//                                   <th style={{ width: evaluationMode === 'parameters' ? "40%" : "70%" }}>Parameter</th>
//                                   {evaluationMode === 'parameters' && <th style={{ width: "30%" }}>Description</th>}
//                                   <th style={{ width: "15%" }}>Rating</th>
//                                   <th style={{ width: "15%" }}>Comments</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {questions.map(q => {
//                                   const controls = renderTableFormControl(q);
//                                   return (
//                                     <tr key={q.id}>
//                                       <td className="align-middle"><strong>{q.question_text}</strong></td>
//                                       {evaluationMode === 'parameters' && <td className="align-middle small text-muted">{q.description}</td>}
//                                       <td className="align-middle">{controls.rating}</td>
//                                       <td className="align-middle">{controls.comments}</td>
//                                     </tr>
//                                   );
//                                 })}
//                               </tbody>
//                             </Table>
//                           </div>
//                         ))
//                       ) : (
//                         <div className="alert alert-info">No {evaluationMode === 'general' ? 'general questions' : 'parameters'} are configured for this role.</div>
//                       )}
                      
//                       {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
//                         <div className="p-4 border rounded bg-light mt-4">
//                             <h4 className="mb-3 text-primary">Final Summary</h4>
//                             <Row>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Strengths</Form.Label>
//                                     <Form.Control as="textarea" rows={3} value={strengths} onChange={e => setStrengths(e.target.value)} placeholder="Key strengths observed..."/>
//                                 </Col>
//                                 <Col md={6} className="mb-3">
//                                     <Form.Label className="fw-bold">Areas for Development</Form.Label>
//                                     <Form.Control as="textarea" rows={3} value={developmentAreas} onChange={e => setDevelopmentAreas(e.target.value)} placeholder="Areas the candidate can improve..."/>
//                                 </Col>
//                             </Row>
//                              <Form.Group className="mb-3">
//                                 <Form.Label className="fw-bold">Final Comments</Form.Label>
//                                 <Form.Control as="textarea" rows={2} value={finalComments} onChange={e => setFinalComments(e.target.value)} placeholder="Overall thoughts..."/>
//                             </Form.Group>
//                              <Form.Group>
//                                 <Form.Label className="fw-bold">Overall Recommendation <span className="text-danger">*</span></Form.Label>
//                                 <Form.Select value={recommendation} onChange={e => setRecommendation(e.target.value)} required>
//                                     <option value="">-- Select Recommendation --</option>
//                                     {RECOMMENDATION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
//                                 </Form.Select>
//                             </Form.Group>
//                         </div>
//                       )}
                      
//                       <div className="text-center mt-4 pb-3">
//                         <Button variant="success" type="submit" className="px-5 py-2" disabled={isSubmitting || isLoadingQuestions || evaluationQuestions.length === 0}>
//                           {isSubmitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Evaluation"}
//                         </Button>
//                       </div>
//                     </>
//                   )}
                  
//                   {!selectedJob && !isLoadingCandidates && <div className="text-center p-5 text-muted"><p>Please select a candidate and job to begin.</p></div>}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .evaluation-table td, .evaluation-table th { vertical-align: middle; }
//         .evaluation-table th { text-align: center; }
//       `}</style>
//     </>
//   )
// }


// import React, { useState, useEffect } from "react";
// import Swal from "sweetalert2";
// import { Form, Button } from "react-bootstrap";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import Select from "react-select";

// export default function EvaluationForm() {
//   const [formData, setFormData] = useState({
//     candidate: "",
//     candidateEmail: "",
//     educationalBackground: "",
//     educationalBackgroundComments: "",
//     priorWorkExperience: "",
//     priorWorkExperienceComments: "",
//     technicalQualifications: "",
//     technicalQualificationsComments: "",
//     verbalCommunication: "",
//     verbalCommunicationComments: "",
//     candidateInterest: "",
//     candidateInterestComments: "",
//     knowledgeOfOrganization: "",
//     knowledgeOfOrganizationComments: "",
//     teambuildingInterpersonalSkills: "",
//     teambuildingInterpersonalSkillsComments: "",
//     initiative: "",
//     initiativeComments: "",
//     timeManagement: "",
//     timeManagementComments: "",
//     overallImpression: "",
//     overallImpressionComments: "",
//   });
//   const [candidates, setCandidates] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);

//   useEffect(() => {
//     fetchCandidates();
//   }, []);

//   const fetchCandidates = async () => {
//     try {
//       const email = localStorage.getItem("email");
//       const response = await fetch("https://raasbackend.vetrinahealthcare.com/interviewers/", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ email: email }),
//       });
//       if (!response.ok) {
//         throw new Error("Failed to fetch candidates");
//       }
//       const data = await response.json();

//       const candidatesData = data.map((candidate) => ({
//         name: candidate.candidate,
//         email: candidate.candidate_email,
//       }));
//       setCandidates(candidatesData);
//     } catch (error) {
//       console.error("Error fetching candidates:", error);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value,
//     });
//   };

//   const handleSubmit = async (e) => {
//     setIsLoading(true); // Start loading
//     e.preventDefault();
//     try {
//       const formDataWithCandidateEmail = {
//         ...formData,
//         candidateEmail: candidates.find(
//           (candidate) => candidate.name === formData.candidate
//         )?.email,
//       };

//       const response = await fetch("https://raasbackend.vetrinahealthcare.com/save_evaluation/", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(formDataWithCandidateEmail),
//       });

//       if (!response.ok) {
//         throw new Error("Failed to save evaluation");
//       }

//       Swal.fire({
//         icon: "success",
//         title: "Great",
//         text: "Candidate evaluated successfully!",
//         timer: 1000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } catch (error) {
//       console.error("Error saving evaluation:", error);

//       Swal.fire({
//         icon: "warning",
//         title: "Oops...",
//         text: "You have already evaluated this candidate!",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });

//     } finally {
//       // Stop the loading spinner
//       setIsLoading(false);
//     }

//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container mt-4  mb-4" >
//             <div
//               className="row justify-content-center "
//               style={{ width: "100%", textAlign: "start" }}
//             >
//               <div className="col-md-8 col-9 auth-inner w-75">
//                 <h2 className="display-6 gradient-text mb-4 text-center">
//                   Evaluation Form
//                 </h2>
//                 <p className="text-success">
//                   Interview evaluation forms are to be completed by the
//                   interviewer to rank the candidate’s overall qualifications for
//                   the position for which they have applied. Under each heading,
//                   the interviewer should give the candidate a rating and write
//                   specific job-related comments in the space provided.
//                 </p>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="candidate">
//                     <Select
//                       options={candidates.map((candidate) => ({
//                         value: candidate.name,
//                         label: candidate.name,
//                       }))}
//                       value={
//                         formData.candidate
//                           ? {
//                             value: formData.candidate,
//                             label: formData.candidate,
//                           }
//                           : null
//                       }
//                       onChange={(selectedOption) =>
//                         setFormData({
//                           ...formData,
//                           candidate: selectedOption?.value || "",
//                         })
//                       }
//                       placeholder="Select Candidate"
//                       isSearchable={true}
//                       styles={{
//                         control: (styles) => ({
//                           ...styles,
//                           textAlign: "start",
//                         }),
//                       }}
//                     />
//                   </Form.Group>

//                   <hr />
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group controlId="educationalBackground">
//                         <Form.Label className="mb-2 text-start">
//                           1. Educational Background
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="educationalBackground"
//                           value={formData.educationalBackground}
//                           onChange={handleChange}
//                           className="form-select"
//                         >
//                           <option value="">Select rating...</option>
//                           <option
//                             value="5"
//                             dropdown-toggle
//                             dropdown-toggle-split
//                           >
//                             Exceptional
//                           </option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="educationalBackgroundComments"
//                           value={formData.educationalBackgroundComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group controlId="priorWorkExperience">
//                         <Form.Label className="mb-2 text-start">
//                           2. Prior Work Experience
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="priorWorkExperience"
//                           value={formData.priorWorkExperience}
//                           onChange={handleChange}
//                           className="form-select"
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="priorWorkExperienceComments"
//                           value={formData.priorWorkExperienceComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="technicalQualifications"
//                         className="mt-4"
//                       >
//                         <Form.Label>
//                           3. Technical Qualifications/Experience
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="technicalQualifications"
//                           value={formData.technicalQualifications}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="technicalQualificationsComments"
//                           value={formData.technicalQualificationsComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="verbalCommunication"
//                         className="mt-4"
//                       >
//                         <Form.Label>4. Verbal Communication</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="verbalCommunication"
//                           value={formData.verbalCommunication}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="verbalCommunicationComments"
//                           value={formData.verbalCommunicationComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="candidateInterest"
//                         className="mt-4"
//                       >
//                         <Form.Label>5. Candidate Interest</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="candidateInterest"
//                           value={formData.candidateInterest}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="candidateInterestComments"
//                           value={formData.candidateInterestComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="knowledgeOfOrganization"
//                         className="mt-4"
//                       >
//                         <Form.Label>6. Knowledge of Organization</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="knowledgeOfOrganization"
//                           value={formData.knowledgeOfOrganization}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="knowledgeOfOrganizationComments"
//                           value={formData.knowledgeOfOrganizationComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="teambuildingInterpersonalSkills"
//                         className="mt-4"
//                       >
//                         <Form.Label>
//                           7. Teambuilding/Interpersonal Skills
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="teambuildingInterpersonalSkills"
//                           value={formData.teambuildingInterpersonalSkills}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="teambuildingInterpersonalSkillsComments"
//                           value={
//                             formData.teambuildingInterpersonalSkillsComments
//                           }
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group controlId="initiative" className="mt-4">
//                         <Form.Label>8. Initiative </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="initiative"
//                           value={formData.initiative}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="initiativeComments"
//                           value={formData.initiativeComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group controlId="timeManagement" className="mt-4">
//                         <Form.Label>9. Time Management</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="timeManagement"
//                           value={formData.timeManagement}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="timeManagementComments"
//                           value={formData.timeManagementComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="overallImpression"
//                         className="mt-4"
//                       >
//                         <Form.Label>
//                           10. Overall Impression and Recommendation
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="overallImpression"
//                           value={formData.overallImpression}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="overallImpressionComments"
//                           value={formData.overallImpressionComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="text-center">
//                     <Button
//                       variant="primary"
//                       type="submit"
//                       className="mt-4 btn-block"
//                       style={{ textAlign: "center" }}
//                       disabled={isLoading}
//                     >

//                       {isLoading ? (
//                         <span
//                           className="spinner-border spinner-border-sm"
//                           role="status"
//                           aria-hidden="true"
//                         ></span>
//                       ) : (
//                         "Submit"
//                       )}

//                     </Button>
//                   </div>
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// import React, { useState, useEffect } from "react";
// import Swal from "sweetalert2";
// import { Form, Button } from "react-bootstrap";
// import Sidebar from "./Sidebar";
// import DashNavbar from "./DashNavbar";
// import Select from "react-select";

// export default function EvaluationForm() {
//   const [formData, setFormData] = useState({
//     candidate: "",
//     candidateEmail: "",
//     educationalBackground: "",
//     educationalBackgroundComments: "",
//     priorWorkExperience: "",
//     priorWorkExperienceComments: "",
//     technicalQualifications: "",
//     technicalQualificationsComments: "",
//     verbalCommunication: "",
//     verbalCommunicationComments: "",
//     candidateInterest: "",
//     candidateInterestComments: "",
//     knowledgeOfOrganization: "",
//     knowledgeOfOrganizationComments: "",
//     teambuildingInterpersonalSkills: "",
//     teambuildingInterpersonalSkillsComments: "",
//     initiative: "",
//     initiativeComments: "",
//     timeManagement: "",
//     timeManagementComments: "",
//     overallImpression: "",
//     overallImpressionComments: "",
//   });
//   const [candidates, setCandidates] = useState([]);

//   useEffect(() => {
//     fetchCandidates();
//   }, []);

//   const fetchCandidates = async () => {
//     try {
//       const email = localStorage.getItem("email");
//       const response = await fetch("https://raasbackend.vetrinahealthcare.com/interviewers/", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ email: email }),
//       });
//       if (!response.ok) {
//         throw new Error("Failed to fetch candidates");
//       }
//       const data = await response.json();

//       const candidatesData = data.map((candidate) => ({
//         name: candidate.candidate,
//         email: candidate.candidate_email,
//       }));
//       setCandidates(candidatesData);
//     } catch (error) {
//       console.error("Error fetching candidates:", error);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value,
//     });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     try {
//       const formDataWithCandidateEmail = {
//         ...formData,
//         candidateEmail: candidates.find(
//           (candidate) => candidate.name === formData.candidate
//         )?.email,
//       };

//       const response = await fetch("https://raasbackend.vetrinahealthcare.com/save_evaluation/", {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(formDataWithCandidateEmail),
//       });

//       if (!response.ok) {
//         throw new Error("Failed to save evaluation");
//       }

//       Swal.fire({
//         icon: "success",
//         title: "Great",
//         text: "Candidate evaluated successfully!",
//         timer: 1000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     } catch (error) {
//       console.error("Error saving evaluation:", error);

//       Swal.fire({
//         icon: "warning",
//         title: "Oops...",
//         text: "You have already evaluated this candidate!",
//         timer: 4000,
//         timerProgressBar: true,
//         showConfirmButton: false,
//       });
//     }
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container mt-4  mb-4" >
//             <div
//               className="row justify-content-center "
//               style={{ width: "100%", textAlign: "start" }}
//             >
//               <div className="col-md-8 col-9 auth-inner w-75">
//                 <h2 className="display-6 gradient-text mb-4 text-center">
//                   Evaluation Form
//                 </h2>
//                 <p className="text-success">
//                   Interview evaluation forms are to be completed by the
//                   interviewer to rank the candidate’s overall qualifications for
//                   the position for which they have applied. Under each heading,
//                   the interviewer should give the candidate a rating and write
//                   specific job-related comments in the space provided.
//                 </p>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="candidate">
//                     <Select
//                       options={candidates.map((candidate) => ({
//                         value: candidate.name,
//                         label: candidate.name,
//                       }))}
//                       value={
//                         formData.candidate
//                           ? {
//                               value: formData.candidate,
//                               label: formData.candidate,
//                             }
//                           : null
//                       }
//                       onChange={(selectedOption) =>
//                         setFormData({
//                           ...formData,
//                           candidate: selectedOption?.value || "",
//                         })
//                       }
//                       placeholder="Select Candidate"
//                       isSearchable={true}
//                       styles={{
//                         control: (styles) => ({
//                           ...styles,
//                           textAlign: "start",
//                         }),
//                       }}
//                     />
//                   </Form.Group>

//                   <hr />
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group controlId="educationalBackground">
//                         <Form.Label className="mb-2 text-start">
//                           1. Educational Background
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="educationalBackground"
//                           value={formData.educationalBackground}
//                           onChange={handleChange}
//                           className="form-select"
//                         >
//                           <option value="">Select rating...</option>
//                           <option
//                             value="5"
//                             dropdown-toggle
//                             dropdown-toggle-split
//                           >
//                             Exceptional
//                           </option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="educationalBackgroundComments"
//                           value={formData.educationalBackgroundComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group controlId="priorWorkExperience">
//                         <Form.Label className="mb-2 text-start">
//                           2. Prior Work Experience
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="priorWorkExperience"
//                           value={formData.priorWorkExperience}
//                           onChange={handleChange}
//                           className="form-select"
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="priorWorkExperienceComments"
//                           value={formData.priorWorkExperienceComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="technicalQualifications"
//                         className="mt-4"
//                       >
//                         <Form.Label>
//                           3. Technical Qualifications/Experience
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="technicalQualifications"
//                           value={formData.technicalQualifications}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="technicalQualificationsComments"
//                           value={formData.technicalQualificationsComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="verbalCommunication"
//                         className="mt-4"
//                       >
//                         <Form.Label>4. Verbal Communication</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="verbalCommunication"
//                           value={formData.verbalCommunication}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="verbalCommunicationComments"
//                           value={formData.verbalCommunicationComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="candidateInterest"
//                         className="mt-4"
//                       >
//                         <Form.Label>5. Candidate Interest</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="candidateInterest"
//                           value={formData.candidateInterest}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="candidateInterestComments"
//                           value={formData.candidateInterestComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="knowledgeOfOrganization"
//                         className="mt-4"
//                       >
//                         <Form.Label>6. Knowledge of Organization</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="knowledgeOfOrganization"
//                           value={formData.knowledgeOfOrganization}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="knowledgeOfOrganizationComments"
//                           value={formData.knowledgeOfOrganizationComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="teambuildingInterpersonalSkills"
//                         className="mt-4"
//                       >
//                         <Form.Label>
//                           7. Teambuilding/Interpersonal Skills
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="teambuildingInterpersonalSkills"
//                           value={formData.teambuildingInterpersonalSkills}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="teambuildingInterpersonalSkillsComments"
//                           value={
//                             formData.teambuildingInterpersonalSkillsComments
//                           }
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group controlId="initiative" className="mt-4">
//                         <Form.Label>8. Initiative </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="initiative"
//                           value={formData.initiative}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="initiativeComments"
//                           value={formData.initiativeComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="row">
//                     <div className="col-md-6">
//                       <Form.Group controlId="timeManagement" className="mt-4">
//                         <Form.Label>9. Time Management</Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="timeManagement"
//                           value={formData.timeManagement}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="timeManagementComments"
//                           value={formData.timeManagementComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                     <div className="col-md-6">
//                       <Form.Group
//                         controlId="overallImpression"
//                         className="mt-4"
//                       >
//                         <Form.Label>
//                           10. Overall Impression and Recommendation
//                         </Form.Label>
//                         <Form.Control
//                           as="select"
//                           name="overallImpression"
//                           value={formData.overallImpression}
//                           onChange={handleChange}
//                         >
//                           <option value="">Select rating...</option>
//                           <option value="5">Exceptional</option>
//                           <option value="4">Above Average</option>
//                           <option value="3">Average</option>
//                           <option value="2">Satisfactory</option>
//                           <option value="1">Unsatisfactory</option>
//                         </Form.Control>
//                         <Form.Control
//                           as="textarea"
//                           rows={2}
//                           name="overallImpressionComments"
//                           value={formData.overallImpressionComments}
//                           onChange={handleChange}
//                           placeholder="Comments..."
//                         />
//                       </Form.Group>
//                     </div>
//                   </div>
//                   <div className="text-center">
//                     <Button
//                       variant="primary"
//                       type="submit"
//                       className="mt-4 btn-block"
//                       style={{ textAlign: "center" }}
//                     >
//                       Submit
//                     </Button>
//                   </div>
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }



// "use client"

// import { useState, useEffect } from "react"
// import Swal from "sweetalert2"
// import { Form, Button, Spinner, Table } from "react-bootstrap"
// import Sidebar from "./Sidebar"
// import DashNavbar from "./DashNavbar"
// import Select from "react-select"
// import axios from "axios"

// // API Base URLs (configurable)
// const API_BASE_URL_CANDIDATES_JOBS = "https://raasbackend.vetrinahealthcare.com/" // For candidate and job title fetching
// const API_BASE_URL_QUESTIONS = "https://raasbackend.vetrinahealthcare.com/" // For questions

// const RATING_SCALE_OPTIONS = [
//   { value: "Exceptional", label: "Exceptional" },
//   { value: "Above Average", label: "Above Average" },
//   { value: "Average", label: "Average" },
//   { value: "Satisfactory", label: "Satisfactory" },
//   { value: "Unsatisfactory", label: "Unsatisfactory" },
// ]

// export default function EvaluationForm() {
//   const [allCandidates, setAllCandidates] = useState([])
//   const [selectedCandidate, setSelectedCandidate] = useState(null)
//   const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)
//   const [appliedJobTitles, setAppliedJobTitles] = useState([])
//   const [selectedJob, setSelectedJob] = useState(null)
//   const [isLoadingJobTitles, setIsLoadingJobTitles] = useState(false)
//   const [evaluationQuestions, setEvaluationQuestions] = useState([])
//   const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
//   const [formData, setFormData] = useState({})
//   const [isSubmitting, setIsSubmitting] = useState(false)

//   useEffect(() => {
//     const fetchInterviewableCandidates = async () => {
//       setIsLoadingCandidates(true)
//       try {
//         const interviewerEmail = localStorage.getItem("email")
//         if (!interviewerEmail) {
//           Swal.fire("Error", "Interviewer email not found. Please log in again.", "error")
//           setIsLoadingCandidates(false)
//           return
//         }

//         const response = await axios.post(`${API_BASE_URL_CANDIDATES_JOBS}/interviewers/`, {
//           email: interviewerEmail,
//         })

//         if (response.data && response.data.length > 0) {
//           const candidatesData = response.data.map((cand) => ({
//             value: cand.candidate_email,
//             label: `${cand.candidate || cand.candidate_name} (Email: ${cand.candidate_email})`,
//             candidateName: cand.candidate || cand.candidate_name,
//           }))
//           setAllCandidates(candidatesData)
//         } else {
//           setAllCandidates([])
//         }
//       } catch (error) {
//         console.error("Error fetching interviewable candidates:", error)
//         Swal.fire("Error", "Failed to fetch candidates for evaluation.", "error")
//       } finally {
//         setIsLoadingCandidates(false)
//       }
//     }

//     fetchInterviewableCandidates()
//   }, [])

//   useEffect(() => {
//     if (selectedCandidate && selectedCandidate.value) {
//       const fetchAppliedJobs = async (candidateEmail) => {
//         setIsLoadingJobTitles(true)
//         setAppliedJobTitles([])
//         setSelectedJob(null)
//         setEvaluationQuestions([])
//         setFormData({})

//         try {
//           const response = await axios.get(`${API_BASE_URL_CANDIDATES_JOBS}/get_applied_job/${candidateEmail}/`)
//           if (response.data) {
//             let jobsData = []
//             // This logic correctly prioritizes job_id and falls back to job_title
//             if (response.data.applied_jobs && Array.isArray(response.data.applied_jobs)) {
//               jobsData = response.data.applied_jobs.map((job) => ({
//                 value: job.job_id, // The value for API calls is the ID
//                 label: job.job_title, // The label for display and title-based API calls
//               }))
//             } else if (response.data.job_titles && Array.isArray(response.data.job_titles)) {
//               const uniqueTitles = [...new Set(response.data.job_titles)]
//               jobsData = uniqueTitles.map((title) => ({ value: title, label: title }))
//             }

//             if (jobsData.length > 0) {
//               setAppliedJobTitles(jobsData)
//             } else {
//               Swal.fire(
//                 "No Applied Jobs",
//                 "This candidate has not applied for any jobs or job data is unavailable.",
//                 "info",
//               )
//             }
//           } else {
//             Swal.fire("No Applied Jobs", "No job application data found for this candidate.", "info")
//           }
//         } catch (error) {
//           console.error("Error fetching applied job titles:", error)
//           Swal.fire("Error", "Failed to fetch applied job titles.", "error")
//         } finally {
//           setIsLoadingJobTitles(false)
//         }
//       }

//       fetchAppliedJobs(selectedCandidate.value)
//     } else {
//       setAppliedJobTitles([])
//       setSelectedJob(null)
//       setEvaluationQuestions([])
//       setFormData({})
//     }
//   }, [selectedCandidate])

//   // =========================================================================================
//   // === MODIFIED SECTION: This useEffect now fetches from both APIs and combines the results ===
//   // =========================================================================================
//   useEffect(() => {
//     if (selectedJob && selectedJob.label) {
//       // Use selectedJob.label which is always the title
//       const fetchAllQuestions = async (jobTitle) => {
//         setIsLoadingQuestions(true)
//         setEvaluationQuestions([])
//         setFormData({})

//         // Define the two API endpoints
//         const simpleQuestionsUrl = `${API_BASE_URL_QUESTIONS}/evaluation_questions/${jobTitle}/`
//         const areaQuestionsUrl = `${API_BASE_URL_QUESTIONS}/jobs/title/${jobTitle}/interviewer-evaluations/list/`

//         try {
//           // Use Promise.allSettled to fetch from both endpoints simultaneously
//           // This is robust: if one fails, the other can still succeed.
//           const results = await Promise.allSettled([axios.get(simpleQuestionsUrl), axios.get(areaQuestionsUrl)])

//           const combinedQuestions = []

//           // Process result from the first API call
//           if (results[0].status === "fulfilled" && Array.isArray(results[0].value.data)) {
//             combinedQuestions.push(...results[0].value.data)
//           } else if (results[0].status === "rejected") {
//             console.error(
//               "Error fetching simple evaluation questions:",
//               results[0].reason.response?.data || results[0].reason.message,
//             )
//           }

//           // Process result from the second API call
//           if (results[1].status === "fulfilled" && Array.isArray(results[1].value.data)) {
//             combinedQuestions.push(...results[1].value.data)
//           } else if (results[1].status === "rejected") {
//             console.error(
//               "Error fetching area evaluation questions:",
//               results[1].reason.response?.data || results[1].reason.message,
//             )
//           }

//           if (combinedQuestions.length > 0) {
//             // A simple way to remove potential duplicates if IDs are shared across tables
//             const uniqueQuestions = Array.from(new Map(combinedQuestions.map((item) => [item.id, item])).values())

//             setEvaluationQuestions(uniqueQuestions)
//             const initialFormData = {}
//             uniqueQuestions.forEach((q) => {
//               initialFormData[`question_${q.id}_answer`] = ""
//               initialFormData[`question_${q.id}_comments`] = ""
//             })
//             setFormData(initialFormData)
//           } else {
//             Swal.fire("No Questions", "No evaluation questions found for this job role from any source.", "info")
//           }
//         } catch (error) {
//           // This catch block is for network errors or issues with Promise.allSettled itself
//           console.error("A critical error occurred while fetching questions:", error)
//           Swal.fire("Error", "A critical error occurred while loading evaluation questions.", "error")
//         } finally {
//           setIsLoadingQuestions(false)
//         }
//       }

//       fetchAllQuestions(selectedJob.label)
//     } else {
//       setEvaluationQuestions([])
//       setFormData({})
//     }
//   }, [selectedJob])

//   // =========================================================================================
//   // === END OF MODIFIED SECTION ===
//   // =========================================================================================

//   const handleCandidateChange = (selectedOption) => {
//     setSelectedCandidate(selectedOption)
//   }

//   const handleJobChange = (selectedOption) => {
//     setSelectedJob(selectedOption)
//   }

//   const handleChange = (questionId, fieldType, value) => {
//     const fieldName = `question_${questionId}_${fieldType}`
//     setFormData((prevData) => ({
//       ...prevData,
//       [fieldName]: value,
//     }))
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     if (!selectedCandidate || !selectedJob) {
//       Swal.fire("Warning", "Please select a candidate and a job title.", "warning")
//       return
//     }

//     if (evaluationQuestions.length === 0) {
//       Swal.fire("Warning", "Evaluation questions are not loaded.", "warning")
//       return
//     }

//     setIsSubmitting(true)

//     const evaluationResults = evaluationQuestions.map((q) => {
//       let answerValue = formData[`question_${q.id}_answer`] || ""
//       const commentsValue = formData[`question_${q.id}_comments`] || ""

//       if (q.answers && q.answers.length === 1 && q.answers[0] === "") {
//         if (!answerValue && commentsValue) {
//           answerValue = commentsValue
//         }
//       }

//       return {
//         question_id: q.id,
//         question_text: q.question_text,
//         area: q.area || null, // Include area if it exists
//         answer_value: answerValue,
//         comments: commentsValue,
//       }
//     })

//     const payload = {
//       candidate_email: selectedCandidate.value,
//       candidate_name: selectedCandidate.candidateName || selectedCandidate.label,
//       job_id: selectedJob.value,
//       job_title: selectedJob.label,
//       interviewer_email: localStorage.getItem("email"),
//       evaluation_details: evaluationResults,
//     }

//     try {
//       const response = await axios.post(`${API_BASE_URL_CANDIDATES_JOBS}/save_evaluation/`, payload)
//       if (response.status === 200 || response.status === 201) {
//         Swal.fire("Success", "Evaluation submitted successfully!", "success")
//         setSelectedCandidate(null)
//       } else {
//         throw new Error(response.data.detail || "Failed to save evaluation.")
//       }
//     } catch (error) {
//       console.error("Error saving evaluation:", error)
//       const errorMessage = error.response?.data?.detail || error.message || "Failed to save evaluation."
//       Swal.fire("Error", errorMessage, "error")
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   const renderTableFormControl = (question) => {
//     const questionId = question.id
//     const answerValue = formData[`question_${questionId}_answer`] || ""
//     const commentsValue = formData[`question_${questionId}_comments`] || ""

//     let qType = "descriptive"
//     if (Array.isArray(question.answers)) {
//       if (question.answers.length === 2 && question.answers.includes("True") && question.answers.includes("False")) {
//         qType = "truefalse"
//       } else if (question.answers.length > 1 && !(question.answers.length === 1 && question.answers[0] === "")) {
//         const isRatingScale = RATING_SCALE_OPTIONS.some((r_opt) => question.answers.includes(r_opt.value))
//         if (isRatingScale || question.answers.length >= 3) {
//           qType = "mcq_rating"
//         }
//       } else if (question.answers.length === 1 && question.answers[0] === "") {
//         qType = "descriptive_textarea"
//       }
//     }

//     switch (qType) {
//       case "mcq_rating":
//         return {
//           rating: (
//             <Form.Control
//               as="select"
//               value={answerValue}
//               onChange={(e) => handleChange(questionId, "answer", e.target.value)}
//               required
//               size="sm"
//             >
//               <option value="">-- Select --</option>
//               {question.answers.map((optionText, idx) => (
//                 <option key={idx} value={optionText}>
//                   {optionText}
//                 </option>
//               ))}
//             </Form.Control>
//           ),
//           comments: (
//             <Form.Control
//               as="textarea"
//               rows={2}
//               value={commentsValue}
//               onChange={(e) => handleChange(questionId, "comments", e.target.value)}
//               placeholder="Comments (optional)"
//               size="sm"
//             />
//           ),
//         }

//       case "truefalse":
//         return {
//           rating: (
//             <div>
//               {question.answers.map((option, idx) => (
//                 <Form.Check
//                   key={idx}
//                   type="radio"
//                   inline
//                   label={option}
//                   name={`question_${questionId}_answer`}
//                   id={`question_${questionId}_option_${idx}`}
//                   value={option}
//                   checked={answerValue === option}
//                   onChange={(e) => handleChange(questionId, "answer", e.target.value)}
//                   required
//                 />
//               ))}
//             </div>
//           ),
//           comments: (
//             <Form.Control
//               as="textarea"
//               rows={2}
//               value={commentsValue}
//               onChange={(e) => handleChange(questionId, "comments", e.target.value)}
//               placeholder="Comments (optional)"
//               size="sm"
//             />
//           ),
//         }

//       case "descriptive_textarea":
//       case "descriptive":
//         return {
//           rating: <span className="text-muted">N/A</span>,
//           comments: (
//             <Form.Control
//               as="textarea"
//               rows={3}
//               value={commentsValue}
//               onChange={(e) => handleChange(questionId, "comments", e.target.value)}
//               placeholder="Provide your detailed response..."
//               required
//               size="sm"
//             />
//           ),
//         }

//       default:
//         return {
//           rating: <span className="text-danger">Unsupported format</span>,
//           comments: <span className="text-danger">Unsupported format</span>,
//         }
//     }
//   }

//   // Group questions by area
//   const groupedQuestions = evaluationQuestions.reduce((groups, question) => {
//     const area = question.area || "General Questions"
//     if (!groups[area]) {
//       groups[area] = []
//     }
//     groups[area].push(question)
//     return groups
//   }, {})

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="container-fluid p-3 d-flex flex-column h-100">
//             <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
//               <div className="card-header bg-primary text-white">
//                 <h2 className="mb-0 h4">Candidate - Interview Evaluation Form</h2>
//               </div>
//               <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
//                 <Form onSubmit={handleSubmit}>
//                   <Form.Group controlId="candidateSelection" className="mb-4">
//                     <Form.Label className="fw-bold">Select Candidate:</Form.Label>
//                     <Select
//                       options={allCandidates}
//                       value={selectedCandidate}
//                       onChange={handleCandidateChange}
//                       placeholder="Select a candidate..."
//                       isLoading={isLoadingCandidates}
//                       isDisabled={isLoadingCandidates || isSubmitting}
//                       isClearable
//                     />
//                   </Form.Group>

//                   {selectedCandidate && (
//                     <Form.Group controlId="jobTitleSelection" className="mb-4">
//                       <Form.Label className="fw-bold">Select Job Applied For:</Form.Label>
//                       <Select
//                         options={appliedJobTitles}
//                         value={selectedJob}
//                         onChange={handleJobChange}
//                         placeholder={isLoadingJobTitles ? "Loading job titles..." : "Select job title..."}
//                         isLoading={isLoadingJobTitles}
//                         isDisabled={isLoadingJobTitles || isSubmitting || !appliedJobTitles.length}
//                         isClearable
//                       />
//                     </Form.Group>
//                   )}

//                   {isLoadingQuestions && (
//                     <div className="text-center my-4">
//                       <Spinner animation="border" variant="primary" />
//                       <p className="mt-2">Loading questions...</p>
//                     </div>
//                   )}

//                   {!isLoadingQuestions && selectedJob && evaluationQuestions.length > 0 && (
//                     <>
//                       <hr className="my-4" />

//                       {/* Header Information */}
//                       <div className="row mb-4 p-3 bg-light rounded">
//                         <div className="col-md-6">
//                           <p>
//                             <strong>Candidate Name:</strong>{" "}
//                             {selectedCandidate?.candidateName || selectedCandidate?.label}
//                           </p>
//                           <p>
//                             <strong>Position Applied For:</strong> {selectedJob?.label}
//                           </p>
//                         </div>
//                         <div className="col-md-6">
//                           <p>
//                             <strong>Interview Date:</strong> {new Date().toLocaleDateString()}
//                           </p>
//                           <p>
//                             <strong>Interviewer:</strong> {localStorage.getItem("email")}
//                           </p>
//                         </div>
//                       </div>

//                       {/* Evaluation Tables by Area */}
//                       {Object.entries(groupedQuestions).map(([area, questions], areaIndex) => (
//                         <div key={areaIndex} className="mb-5">
//                           <h4 className="mb-3 text-primary">
//                             {areaIndex + 1}. 🔍 {area}
//                           </h4>

//                           <Table striped bordered hover responsive className="evaluation-table">
//                             <thead className="table-dark">
//                               <tr>
//                                 <th style={{ width: "50%" }}>Competency</th>
//                                 <th style={{ width: "25%" }}>Rating</th>
//                                 <th style={{ width: "25%" }}>Comments</th>
//                               </tr>
//                             </thead>
//                             <tbody>
//                               {questions.map((question, questionIndex) => {
//                                 const controls = renderTableFormControl(question)
//                                 return (
//                                   <tr key={question.id || questionIndex}>
//                                     <td className="align-middle">
//                                       <strong>{question.question_text}</strong>
//                                       {question.description && (
//                                         <div className="text-muted small mt-1">{question.description}</div>
//                                       )}
//                                     </td>
//                                     <td className="align-middle">{controls.rating}</td>
//                                     <td className="align-middle">{controls.comments}</td>
//                                   </tr>
//                                 )
//                               })}
//                             </tbody>
//                           </Table>
//                         </div>
//                       ))}

//                       <div className="text-center mt-4 pb-3">
//                         <Button
//                           variant="success"
//                           type="submit"
//                           className="px-5 py-2"
//                           disabled={isSubmitting || isLoadingCandidates || isLoadingJobTitles || isLoadingQuestions}
//                         >
//                           {isSubmitting ? (
//                             <>
//                               <Spinner
//                                 as="span"
//                                 animation="border"
//                                 size="sm"
//                                 role="status"
//                                 aria-hidden="true"
//                                 className="me-2"
//                               />
//                               Submitting...
//                             </>
//                           ) : (
//                             "Submit Evaluation"
//                           )}
//                         </Button>
//                       </div>
//                     </>
//                   )}

//                   {!isLoadingQuestions && selectedJob && evaluationQuestions.length === 0 && !isLoadingJobTitles && (
//                     <div className="alert alert-info my-4">
//                       No evaluation questions are available for the selected job.
//                     </div>
//                   )}

//                   {(!selectedCandidate || !selectedJob) &&
//                     !isLoadingCandidates &&
//                     !isLoadingJobTitles &&
//                     evaluationQuestions.length === 0 && (
//                       <div className="text-center p-5 text-muted">
//                         <p>Please select a candidate and a job to view evaluation questions.</p>
//                       </div>
//                     )}
//                 </Form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <style jsx>{`
//         .evaluation-table td {
//           vertical-align: middle;
//         }
//         .evaluation-table th {
//           text-align: center;
//           vertical-align: middle;
//         }
//         .evaluation-table .form-control,
//         .evaluation-table .form-select {
//           border: 1px solid #ced4da;
//         }
//         .evaluation-table .form-control:focus,
//         .evaluation-table .form-select:focus {
//           border-color: #86b7fe;
//           box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
//         }
//       `}</style>
//     </>
//   )
// }
